import { debounce } from 'lodash';
import { CRDTServiceWS, crdtServiceWS } from 'services/crdt/crdtServiceWS';
import { WebsocketProvider } from 'y-websocket';
import * as Y from 'yjs';
import { DOMSerializer, DOMParser as PMDOMParser } from 'prosemirror-model';
import { createFileLogger, formatJSON } from '../../../../utils/logger';
import { entityService } from '../../../../services/entityService';
import { userService } from '../../../../services/users/userService';
import { initProseMirrorDoc } from 'y-prosemirror';
// CRITICAL: Import LibraryApp plugin BEFORE schema to ensure citation mark is registered
import '../../../LibraryApp/integration/initializeLibraryPlugin';
import { schema as baseSchema } from '../../editor/schema';
import { schemaExtensionRegistry } from '../../plugins/schemaExtensionRegistry';
import { XmlFragment } from '../../../../shared/yjs';
import { createYElement } from '../doc/internal/yDocLowLevelAPI';
import { summaryService } from '../features/ai/summaryService';
import { replaceWithMarkdown as docReplaceWithMarkdown } from '../doc/docApi';
import { IS_DEV } from '../../../../utils/devMode';
import { parseJwtPayload } from '../../../../utils/jwtUtils';
import useSpaceStore from '../../../../stores/spaceStore';



const log = createFileLogger('DOCSCRDTSERVICE_APP', { enabled: false });

/** Server is durability boundary; omit base64 from client merge payloads */
const SERVER_IS_DURABILITY_BOUNDARY = true;

// Build extended schema with all plugin marks/nodes
const schema = schemaExtensionRegistry.getRegisteredMarks().length > 0 || schemaExtensionRegistry.getRegisteredNodes().length > 0
  ? schemaExtensionRegistry.buildExtendedSchema(baseSchema)
  : baseSchema;

log.log('[docsCRDTService] Using schema with marks:', {
  marks: Object.keys(schema.marks),
  hasCitation: !!schema.marks.citation
});


// 🔥 PERFORMANCE: Only log debug info in development
const debugLog = IS_DEV ? log.log.bind(log) : () => {};

// Welcome image from public folder
const WELCOME_IMAGE_URL = '/welcomeDocImg.png';


interface CRDTUpdate<T> {
  data: T;
  type: string;
  timestamp: number;
}

interface DocUpdate {
  content: string;
}

// Global initialization lock to prevent Y.js corruption
const globalInitializationLock = new Map<string, Promise<any>>();

export class DocsCRDTService {
  private yContentCache = new Map<string, Y.XmlFragment>();
  private activeDocObservers = new Map<string, () => void>();
  private saveSuppressionUntil = new Map<string, number>();
  private initializationPromises = new Map<string, Promise<{doc: Y.Doc, provider?: WebsocketProvider}>>();
  private currentDocId: string | null = null;
  
  // ✅ CRITICAL: Cache entity types for correct WebSocket room naming
  // Note entities use 'note:' prefix, Document entities use 'doc:' prefix
  private entityTypes = new Map<string, string>();
  
  // ✅ CRITICAL: Prevent infinite loop when getTextContent() triggers Y.js transactions
  // Use Set<any> to store fragment object references as keys
  private isInTextExtraction = new Set<any>();
  
  // ✅ Track previous document lengths for deletion detection
  private previousLengths = new Map<string, number>();
  
  // 🛡️ FLOOD DETECTION: Monitor connection patterns to detect bugs
  private connectionAttempts = new Map<string, number>();
  private recentConnectionTimestamps: number[] = [];
  private readonly CONNECTION_WARNING_THRESHOLD = 3;
  private readonly FLOOD_DETECTION_WINDOW_MS = 5000; // 5 seconds
  private readonly FLOOD_THRESHOLD = 20; // 20 connections in 5 seconds = potential flood

  // 🛡️ CORRUPTION TRACKING: Track documents with corrupted base64 to prevent data loss
  private corruptedDocuments = new Set<string>();
  
  // 🔧 FIX: Per-document debounced save functions to prevent argument clobbering
  // When a single debounce is shared, multiple docs within 500ms cause only the LAST one to save!
  private perDocumentDebouncedSaves = new Map<string, ReturnType<typeof debounce>>();
  
  // 🔥 RECONNECT THROTTLE: Prevent spam saves on frequent reconnections
  private lastReconnectSaveTime = new Map<string, number>();
  
  // ✅ SMART CACHE VERSION TRACKING: Track document versions for deterministic stale detection
  // Maps docId → version number
  // 
  // HOW IT WORKS:
  // - Backend increments version on every save (atomic $inc)
  // - Frontend tracks version in memory and compares with backend
  // - If backend version > cached version → recreate Y.Doc from backend (stale cache)
  // - If versions match → reuse cached Y.Doc (fast path)
  // 
  // BACKWARDS COMPATIBILITY (No migration needed!):
  // - Old docs without version field: implicitly version 0
  // - First save with new code: MongoDB $inc creates field and sets to 1
  // - System self-heals: old docs get version field on first save
  // - Missing version = 0: won't trigger false stale detection (backendVersion > 0 check)
  private docVersions = new Map<string, number>();
  
  // ⚡ PERFORMANCE: Version check cache with TTL (reduces API calls)
  private versionCache = new Map<string, { version: number; timestamp: number }>();
  private readonly VERSION_CACHE_TTL = 5000; // 5 seconds
  
  // Track expected document sizes from backend to detect WebSocket content loss
  private expectedDocumentSizes = new Map<string, number>();
  
  // Track last local edit time to prevent recovery from overwriting user's work
  private lastLocalEditTime = new Map<string, number>();
  
  // ✅ STALE VERSION RECREATION TRACKING: Track when we recreate due to stale version
  // Used to detect if WebSocket sync overwrites freshly loaded backend content
  // Maps docId → { backendVersion, cachedVersion, timestamp, expectedElementCount? }
  private recreatingFromStaleVersion = new Map<string, {
    backendVersion: number;
    cachedVersion: number;
    timestamp: number;
    expectedElementCount?: number;
    initialBase64?: string;
  }>();
  
  // 📊 PERFORMANCE MONITORING: Track lock acquisition times
  private lockMetrics = new Map<string, number>();

  constructor(private crdt: CRDTServiceWS) {
    if (typeof window !== 'undefined') {
      // Flush all pending saves when the tab becomes hidden. This covers tab-switch,
      // window minimise, and most tab-close scenarios — and fires *before* the page
      // starts tearing down, so async HTTP requests can still complete normally.
      const visibilityHandler = () => {
        if (document.visibilityState === 'hidden') {
          for (const [, debouncedFn] of this.perDocumentDebouncedSaves.entries()) {
            try {
              debouncedFn.flush();
            } catch (e) {
              // Non-fatal — WS server will persist within 3s of the last edit
            }
          }
        }
      };
      document.addEventListener('visibilitychange', visibilityHandler);

      // Also flush on beforeunload as a last-resort safety net (async requests
      // may be aborted by the browser, but visibilitychange above handles most cases).
      window.addEventListener('beforeunload', () => {
        for (const [, debouncedFn] of this.perDocumentDebouncedSaves.entries()) {
          try {
            debouncedFn.flush();
          } catch (e) {
            // Ignore errors during unload
          }
        }
      });
    }
  }

  /**
   * Helper: Execute a promise with timeout protection
   */
  private async withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    operation: string
  ): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(
          () => reject(new Error(`${operation} timeout after ${timeoutMs}ms`)),
          timeoutMs
        )
      )
    ]);
  }

  // Set up user awareness for collaborative cursors
  private async setupUserAwareness(provider?: WebsocketProvider, docId?: string, doc?: Y.Doc) {
    if (!provider?.awareness) {
      log.warn('[DocsApp - CRDT] No awareness object available on provider');
      return;
    }

    try {
      // Idempotence and cleanup of previous setup if any
      const anyProvider = provider as any;
      try {
        if (anyProvider.__awarenessCleanup) {
          anyProvider.__awarenessCleanup();
        }
      } catch {}
      if (anyProvider.__awarenessSetupActive) {
        // Already set up; avoid stacking listeners/handlers
        return;
      }

      // Get current user information from multiple sources
      let userId = 'anonymous';
      let userName = 'Anonymous User';
      let userEmail = '';
      
      // Method 1: Try to get user info from Auth0 context (most reliable)
      try {
        const { user: auth0User } = (window as any).__AUTH0_CONTEXT__ || {};
        if (auth0User) {
          userId = auth0User.sub || 'anonymous';
          userName = auth0User.name || auth0User.email || 'Anonymous User';
          userEmail = auth0User.email || '';
          debugLog('[DocsApp - CRDT] Got user from Auth0 context:', { userId: userId.slice(-8), userName });
        }
      } catch (auth0Error) {
        log.warn('[DocsApp - CRDT] Could not access Auth0 context:', auth0Error);
      }
      
      // Method 2: Try legacy window.__auth0User
      if (userId === 'anonymous') {
        const auth0User = (window as any).__auth0User;
        if (auth0User) {
          userId = auth0User.sub || 'anonymous';
          userName = auth0User.name || auth0User.email || 'Anonymous User';
          userEmail = auth0User.email || '';
          debugLog('[DocsApp - CRDT] Got user from window.__auth0User:', { userId: userId.slice(-8), userName });
        }
      }
      
      // Method 3: Try to get user info from localStorage token
      if (userId === 'anonymous') {
        const token = localStorage.getItem('authToken');
        if (token) {
          try {
            const payload = parseJwtPayload(token);
            if (payload) {
              userId = payload.sub || 'anonymous';
              userName = payload.name || payload.email || 'Anonymous User';
              userEmail = payload.email || '';
            }
            debugLog('[DocsApp - CRDT] Got user from localStorage token:', { userId: userId.slice(-8), userName });
          } catch (error) {
            log.error('[DocsApp - CRDT] Error parsing auth token for awareness:', error);
          }
        }
      }
      
      // 🔥 Method 4: Fetch user profile to get real name/email (JWT often missing these)
      if (userId !== 'anonymous') {
        try {
          const userProfile = await userService.getUserProfile();
          if (userProfile?.skeleton) {
            const profileName = userProfile.skeleton.name || 
                              userProfile.skeleton.auth0?.name || 
                              userProfile.skeleton.auth0?.nickname;
            const profileEmail = userProfile.skeleton.email || 
                               userProfile.skeleton.auth0?.email;
            
            // Use profile name if available, otherwise use email as fallback
            if (profileName) {
              userName = profileName;
            } else if (profileEmail) {
              userName = profileEmail;  // 🔥 Email as name fallback
            }
            
            if (profileEmail) {
              userEmail = profileEmail;
            }
          }
        } catch (profileError) {
          // Continue with token data if profile fetch fails
          log.warn('[DocsApp - CRDT] Could not fetch user profile for awareness:', profileError);
        }
      }
      
      // Generate a consistent color for the user based on their ID
      const getUserColor = (id: string) => {
        const colors = [
          '#3b82f6', '#ef4444', '#10b981', '#f59e0b', 
          '#8b5cf6', '#06b6d4', '#84cc16', '#f97316',
          '#ec4899', '#6366f1', '#14b8a6', '#f472b6'
        ];
        const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return colors[hash % colors.length];
      };
      
      // Set the local awareness state for this user
      const userAwarenessState = {
        user: {
          id: userId,
          name: userName,
          email: userEmail,
          color: getUserColor(userId)
        }
      };
      
      provider.awareness.setLocalState(userAwarenessState);
      
      // Verify the awareness state was set correctly
      const verifyState = provider.awareness.getLocalState();
      debugLog('[DocsApp - CRDT] User awareness state set and verified:', {
        userId: userId.slice(-8), // Show last 8 chars for privacy
        userName,
        userEmail,
        color: getUserColor(userId),
        awarenessSetSuccessfully: !!verifyState?.user,
        verifiedUser: verifyState?.user,
        totalAwarenessStates: provider.awareness.getStates().size,
        providerConnected: provider.wsconnected,
        providerSynced: provider.synced,
        roomName: provider.roomname,
        websocketUrl: provider.url
      });
      
      // Add awareness change listener to debug multiplayer connectivity
      const awarenessChangeHandler = ({ added, updated, removed }: { added: number[], updated: number[], removed: number[] }) => {
        const totalStates = provider.awareness.getStates().size;
        
        // Reduce noisy console logs; only surface when visible and not our own awareness
        try {
          const localClientId = (provider.awareness as any).clientID;
          const selfLeft = removed.includes(localClientId);
          if (added.length > 0 && totalStates > 1 && document.visibilityState === 'visible') {
            log.log('[DocsApp - CRDT] Multiplayer detected (another user joined)');
          }
          if (removed.length > 0 && document.visibilityState === 'visible' && !selfLeft) {
            log.log('[DocsApp - CRDT] A remote user left the document');
          }
        } catch {}
      };
      
      provider.awareness.on('change', awarenessChangeHandler);
      // Also add a WebSocket message listener to see if messages are being received
      const originalOnMessage = provider.ws?.onmessage;
      if (provider.ws) {
        provider.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'awareness') {
              debugLog('[DocsApp - CRDT] 📡 Received awareness message from server:', {
                type: data.type,
                clientsInMessage: Object.keys(data.awareness || {}).length,
                roomName: provider.roomname
              });
            }
          } catch (e) {
            // Not JSON or not awareness message
          }
          
          // Call original handler with error handling for corrupted Yjs messages
          if (originalOnMessage && provider.ws) {
            try {
              originalOnMessage.call(provider.ws, event);
            } catch (decodeError) {
              // Gracefully handle corrupted WebSocket messages (e.g., "Unexpected end of array")
              // This can happen when Yjs binary messages are incomplete/corrupted
              console.warn('[DocsApp - CRDT] ⚠️ WebSocket message decode error (non-fatal):', {
                error: decodeError instanceof Error ? decodeError.message : String(decodeError),
                roomName: provider.roomname
              });
              // Don't crash - Yjs will re-sync on next update
            }
          }
        };
        // Track wrapper/original for proper cleanup
        anyProvider.__originalWsOnMessage = originalOnMessage || null;
        anyProvider.__wsOnMessageWrapped = true;
      }
      
      // Add periodic awareness heartbeat to keep connection alive
      const awarenessHeartbeat = setInterval(() => {
        // Only send heartbeat if tab is visible and we have a valid user
        if (document.visibilityState === 'visible' && userId !== 'anonymous') {
          const currentLocal = provider.awareness.getLocalState();
          if (!currentLocal?.user) {
            debugLog('[DocsApp - CRDT] 💓 Heartbeat: Restoring lost awareness state');
            provider.awareness.setLocalState(userAwarenessState);
          }
        }
      }, 10000); // Check every 10 seconds
      
      // Store cleanup functions for this provider
      const cleanupFunctions = () => {
        // REMOVED: Event listener cleanup (listeners were removed to fix spam)
        provider.awareness.off('change', awarenessChangeHandler);
        clearInterval(awarenessHeartbeat);
        try {
          if (provider.ws && anyProvider.__wsOnMessageWrapped) {
            provider.ws.onmessage = anyProvider.__originalWsOnMessage || null;
          }
        } catch {}
        anyProvider.__wsOnMessageWrapped = false;
        anyProvider.__awarenessSetupActive = false;
      };
      
      // Store cleanup function on the provider for later cleanup
      anyProvider.__awarenessCleanup = cleanupFunctions;
      anyProvider.__awarenessSetupActive = true;
      
      // Method 4: Try userStore as fallback and update awareness if better data becomes available
      if (userId === 'anonymous') {
        import('../../../../stores/userStore').then(({ default: useUserStore }) => {
          const userState = useUserStore.getState();
          if (userState.auth0Id) {
            const storeUserId = userState.auth0Id;
            const storeUserName = userState.profile?.skeleton?.name || userState.profile?.skeleton?.auth0?.name || 'User';
            const storeUserEmail = userState.profile?.skeleton?.auth0?.email || '';
            
            // Update awareness with userStore data
            provider.awareness.setLocalState({
              user: {
                id: storeUserId,
                name: storeUserName,
                email: storeUserEmail,
                color: getUserColor(storeUserId)
              }
            });
            
            debugLog('[DocsApp - CRDT] Updated awareness with userStore data:', { 
              userId: storeUserId.slice(-8), 
              userName: storeUserName 
            });
          }
        }).catch(storeError => {
          log.warn('[DocsApp - CRDT] Could not access userStore:', storeError);
        });
      }
      
    } catch (error) {
      log.error('[DocsApp - CRDT] Error setting user awareness state:', error);
    }
  }

  /**
   * Get cached Y.Doc if already initialized, without triggering initialization
   * Used by contextStore to avoid double initialization
   * Returns the promise if document is being/has been initialized, null otherwise
   */
  getCachedYDoc(docId: string): Promise<{ doc: Y.Doc; provider?: WebsocketProvider }> | null {
    return this.initializationPromises.get(docId) || null;
  }

  async getYDocForDoc(
    docId: string, 
    initialContent?: string, 
    initialBase64?: string, 
    initialName?: string, 
    includeWelcomeImage?: boolean,
    pageMargins?: { top: string; right: string; bottom: string; left: string }
  ): Promise<{doc: Y.Doc, provider?: WebsocketProvider}> {
    const crdtStartTime = Date.now();
    const crdtTraceId = `${docId.slice(-8)}_${Date.now()}`;
    
    // Track current document
    this.currentDocId = docId;
    
    log.log(`🔵 [CRDT_TRACE:${crdtTraceId}] === CRDT.GETYDO CFORDOC START ===`, {
      docId: docId.slice(-8),
      hasBase64: !!initialBase64,
      base64Length: initialBase64?.length || 0,
      hasContent: !!initialContent,
      contentLength: typeof initialContent === 'string' ? initialContent.length : 0,
      name: initialName || 'unnamed',
      includeWelcomeImage,
      timestamp: new Date().toISOString(),
      traceId: crdtTraceId
    });
    
    // 🛡️ FLOOD DETECTION: Monitor connection patterns (non-blocking)
    const now = Date.now();
    this.recentConnectionTimestamps.push(now);
    this.recentConnectionTimestamps = this.recentConnectionTimestamps.filter(
      ts => now - ts < this.FLOOD_DETECTION_WINDOW_MS
    );
    
    // Alert if we detect a flood pattern (but don't block - existing deduplication handles it)
    if (this.recentConnectionTimestamps.length > this.FLOOD_THRESHOLD) {
      console.error(
        `🚨 [FLOOD-DETECTED] ${this.recentConnectionTimestamps.length} connection attempts in ${this.FLOOD_DETECTION_WINDOW_MS}ms!`,
        'This indicates a bug causing unnecessary WebSocket connections. Check for loops calling getYDocForDoc.'
      );
    }
    
    // Track repeated attempts for same document
    const attemptCount = (this.connectionAttempts.get(docId) || 0) + 1;
    this.connectionAttempts.set(docId, attemptCount);
    
    if (attemptCount > this.CONNECTION_WARNING_THRESHOLD) {
      console.warn(
        `⚠️ [DUPLICATE-CONNECTION] Document ${docId.slice(-8)} connection attempt #${attemptCount}.`,
        'Existing deduplication should handle this, but multiple attempts detected.'
      );
    }
    
    // Clean up old attempt counters (after 30 seconds)
    setTimeout(() => {
      this.connectionAttempts.delete(docId);
    }, 30000);
    
    // ✅ CRITICAL FIX: Fetch entity FIRST to get entity type for correct room name!
    // Note entities MUST use 'note:' prefix, not 'doc:', for WebSocket persistence!
    let entityType: string | undefined = this.entityTypes.get(docId);
    
    if (!entityType) {
      try {
        const entity = await entityService.get(docId);
        entityType = entity?.entityType;
        
        // Cache it for future use (cleanup, getProvider, etc.)
        if (entityType) {
          this.entityTypes.set(docId, entityType);
        }
        
        console.warn(`🔍 [ENTITY-TYPE] ${docId.slice(-8)} is type: ${entityType} (cached for future use)`);
        console.warn(`🔍 [ENTITY-TYPE] Will use WebSocket room: ${entityType === 'Note' ? 'note:' : 'doc:'}${docId.slice(-8)}`);
      } catch (err) {
        console.warn(`⚠️ [ENTITY-TYPE] Failed to fetch entity type for ${docId.slice(-8)}, defaulting to Document`);
        entityType = 'Document';  // Explicit default
      }
    } else {
      console.warn(`🔍 [ENTITY-TYPE] ${docId.slice(-8)} type cached: ${entityType}`);
      console.warn(`🔍 [ENTITY-TYPE] Will use WebSocket room: ${entityType === 'Note' ? 'note:' : 'doc:'}${docId.slice(-8)}`);
    }
    
    // ✅ FIX: Check if document already exists to prevent creating multiple instances
    const docKey = this.getDocKey(docId, entityType);
    const existingDoc = this.crdt.getYDoc(docKey);
    const existingProvider = this.crdt.getProvider(docKey);
    
    log.log(`🔍 [CRDT_TRACE:${crdtTraceId}] Checking for existing Y.Doc:`, {
      docKey,
      hasExistingDoc: !!existingDoc,
      hasExistingProvider: !!existingProvider,
      providerConnected: existingProvider?.wsconnected,
      providerSynced: existingProvider?.synced
    });
    
    if (existingDoc) {
      // Document exists in cache
      log.log(`[DocsCRDT] Found existing Y.Doc for ${docId.slice(-8)}, checking if stale...`);
      
      const yxml = existingDoc.getXmlFragment('prosemirror');
      
      log.log(`📊 [CRDT_TRACE:${crdtTraceId}] Existing Y.Doc state:`, {
        yxmlLength: yxml.length,
        hasInitialBase64: !!initialBase64,
        initialBase64Length: initialBase64?.length || 0
      });
      
      // ✅ STEP 1: Check for complete corruption (empty doc with backend content)
      if (yxml.length === 0 && initialBase64 && initialBase64.length > 100) {
        console.error(`🚨 [EMPTY-YDOC] ${docId.slice(-8)} - Y.Doc is empty but backend has content!`);
        console.error(`🚨 [CORRUPTION] This indicates Y.Doc corruption - recreating from backend`);
        console.error(`🚨 [CRDT_TRACE:${crdtTraceId}] CORRUPTION DETECTED - will recreate`);
        
        try {
          // Disconnect and destroy old provider
          if (existingProvider) {
            existingProvider.disconnect();
            existingProvider.destroy();
            console.warn(`✅ [RECREATE] Old provider disconnected for ${docId.slice(-8)}`);
          }
          
          // Delete corrupted Y.Doc from cache
          this.crdt.disconnect(docKey);
          console.warn(`✅ [RECREATE] Empty Y.Doc deleted from cache for ${docId.slice(-8)}`);
          console.warn(`🔄 [RECREATE] Will create fresh Y.Doc with backend's correct state...`);
          
          // ✅ CRITICAL: Don't return here - fall through to create NEW Y.Doc below
          
        } catch (cleanupError) {
          console.error(`❌ [RECREATE] Failed to cleanup empty Y.Doc for ${docId.slice(-8)}:`, cleanupError);
          // Continue anyway to try creating fresh doc
        }
      }
      // ✅ STEP 2: SMART CACHE - Version-based stale detection (deterministic)
      // 🚀 PERFORMANCE: Use lightweight version endpoint instead of fetching full document
      // This saves ~500KB-2MB I/O per cache validation check
      else {
        let backendVersion = 0;
        
        try {
          // ⚡ PERFORMANCE: Check cache first (5-second TTL)
          const cached = this.versionCache.get(docId);
          const now = Date.now();
          
          if (cached && now - cached.timestamp < this.VERSION_CACHE_TTL) {
            backendVersion = cached.version;
            log.log(`⚡ [CRDT_TRACE:${crdtTraceId}] Version cache HIT: ${backendVersion} (age: ${now - cached.timestamp}ms)`);
          } else {
            // 🚀 OPTIMIZED: Only fetch version number, not full document with base64
            const versionResponse = await entityService.getVersion(docId);
            backendVersion = versionResponse?.version || 0;
            this.versionCache.set(docId, { version: backendVersion, timestamp: now });
            log.log(`⚡ [CRDT_TRACE:${crdtTraceId}] Version fetched from backend: ${backendVersion}`);
          }
        } catch (fetchError) {
          console.warn(`⚠️ Failed to fetch version for cache check (${docId.slice(-8)}):`, fetchError);
          // Fall back to reusing cached doc if fetch fails
          return { doc: existingDoc, provider: existingProvider };
        }
        
        const cachedVersion = this.docVersions.get(docId) || 0;
        
        log.log(`🔍 [CRDT_TRACE:${crdtTraceId}] Version comparison:`, {
          backendVersion,
          cachedVersion,
          isStale: backendVersion > cachedVersion && backendVersion > 0
        });
        
        // ✅ DETERMINISTIC: Compare versions
        // backendVersion > 0 check prevents false positives for old docs without version field
        if (backendVersion > cachedVersion && backendVersion > 0) {
          console.warn(`🔄 [STALE-VERSION] ${docId.slice(-8)} - Cached v${cachedVersion}, Backend v${backendVersion}`);
          console.warn(`🔄 [STALE-VERSION] Backend has newer version - recreating Y.Doc from backend state`);
          console.warn(`🔄 [CRDT_TRACE:${crdtTraceId}] STALE VERSION - will recreate Y.Doc`);
          
          try {
            // Disconnect and destroy old provider
            if (existingProvider) {
              existingProvider.disconnect();
              existingProvider.destroy();
            }
            
            // Delete stale Y.Doc from cache
            this.crdt.disconnect(docKey);
            
            // Update version tracker with backend version
            this.docVersions.set(docId, backendVersion);
            
            // Now fetch full document with base64 only if we need to recreate
            // This is the only time we actually need the full content
            try {
              const backendEntity = await entityService.get(docId);
              if (backendEntity?.skeleton?.base64) {
                initialBase64 = backendEntity.skeleton.base64;
              }
              
              // ✅ CRITICAL: Mark that we're recreating due to stale version
              this.recreatingFromStaleVersion.set(docId, {
                backendVersion,
                cachedVersion,
                timestamp: Date.now(),
                initialBase64: backendEntity?.skeleton?.base64
              });
            } catch (fetchError) {
              console.warn(`⚠️ Failed to fetch full document for stale recreation:`, fetchError);
              // Continue with whatever initialBase64 was passed in
            }
            
            // ✅ CRITICAL: Fall through to create NEW Y.Doc below with backend state
            
          } catch (cleanupError) {
            console.error(`❌ [STALE-RECREATE] Failed to cleanup stale Y.Doc for ${docId.slice(-8)}:`, cleanupError);
            // Continue anyway to try creating fresh doc
          }
        } else {
          // Version is same or newer in cache - safe to reuse
      return { doc: existingDoc, provider: existingProvider };
        }
      }
    }
    
    // ✅ FIX: Check if initialization is already in progress to prevent race conditions
    if (this.initializationPromises.has(docId)) {
      log.log(`⚡ [CRDT_TRACE:${crdtTraceId}] Reusing in-progress initialization for ${docId.slice(-8)}`);
      debugLog('[getYDocForDoc] ⏳ Initialization already in progress, waiting for completion:', docId);
      // ✅ SMART DEDUPLICATION: Wait for the existing initialization to complete
      // This reuses work instead of creating duplicate Y.Docs
      return await this.initializationPromises.get(docId)!;
    }
    
    // ✅ CORRUPTION PREVENTION: Global lock to prevent simultaneous initialization
    if (globalInitializationLock.has(docId)) {
      log.log(`🔒 [CRDT_TRACE:${crdtTraceId}] === WAITING FOR GLOBAL LOCK ===`);
      log.log('🚨 [CRDT-DEBUG] Document initialization already in progress globally, waiting...', docId.slice(-8));
      const lockStartTime = Date.now();
      
      try {
        // ✅ Wait for lock with 10-second timeout
        await this.withTimeout(
          globalInitializationLock.get(docId)!,
          10000,
          `Lock wait for ${docId.slice(-8)}`
        );
        
        const lockWaitTime = Date.now() - lockStartTime;
        log.log(`🔓 [CRDT_TRACE:${crdtTraceId}] Lock released after ${lockWaitTime}ms`);
        
        // After waiting, check if document now exists
        const nowExistingDoc = this.crdt.getYDoc(docKey);
        const nowExistingProvider = this.crdt.getProvider(docKey);
        if (nowExistingDoc) {
          log.log('🚨 [CRDT-DEBUG] Document now exists after waiting, reusing:', {
            docId: docId.slice(-8),
            yxmlLength: nowExistingDoc.getXmlFragment('prosemirror').length
          });
          return { doc: nowExistingDoc, provider: nowExistingProvider };
        }
      } catch (lockError) {
        console.error(`⚠️ [CRDT] Lock wait failed for ${docId.slice(-8)}:`, lockError);
        // Force-remove stale lock and continue to create fresh doc
        globalInitializationLock.delete(docId);
        log.log(`🔓 [CRDT_TRACE:${crdtTraceId}] Stale lock force-removed, will create fresh doc`);
      }
    }
    
    // Creating NEW Y.js doc and service state (verbose logs removed)
    
    // ✅ CRITICAL FIX: ALWAYS delay WebSocket connection until backend content is applied
    // This ensures backend (source of truth) is loaded first, preventing WebSocket from serving stale data
    const isRecreatingFromStale = this.recreatingFromStaleVersion.has(docId);
    
    log.log(`🔧 [CRDT_TRACE:${crdtTraceId}] Creating NEW Y.Doc and provider...`);
    
    // Set global lock for this document
    const initPromise = (async () => {
      log.log(`🔒 [CRDT_TRACE:${crdtTraceId}] Setting global lock...`);
      
      // ✅ ALWAYS create provider disconnected - connect after backend content is applied
      const doc = this.crdt.ensureInitialized(docKey, {
        connectImmediately: false  // ← Changed: ALWAYS false (was: !isRecreatingFromStale)
      });
      
      log.log(`✅ [CRDT_TRACE:${crdtTraceId}] Y.Doc created (provider DISCONNECTED)`);
      console.warn(`[TEST] 🔌 WEBSOCKET STATUS - Provider created DISCONNECTED`);
      console.warn(`[TEST] 🔌 Will connect WebSocket AFTER backend content is applied (source of truth)`);
      console.warn(`[TEST] ⏱️ Current time: ${new Date().toISOString()}`);
      if (isRecreatingFromStale) {
        console.warn(`🔄 [STALE-RECONNECT] ${docId.slice(-8)} - Recreating from stale version detected`);
      }
      log.log('[getYDocForDoc] Provider created disconnected', { docId: docId.slice(-8), isRecreatingFromStale });
      
      // Y.js doc created, getting provider (verbose logs removed)
      
      const provider = this.crdt.getProvider(docKey);
      log.log(`📡 [CRDT_TRACE:${crdtTraceId}] Provider retrieved:`, {
        hasProvider: !!provider,
        connected: provider?.wsconnected || false,
        synced: provider?.synced || false
      });
      return { doc, provider };
    })();
    
    globalInitializationLock.set(docId, initPromise);
    
    // 📊 Track lock acquisition time for monitoring
    this.lockMetrics.set(docId, Date.now());
    
    // ✅ CRITICAL: Wrap in try/finally to ensure lock cleanup on error
    let doc: Y.Doc;
    let provider: WebsocketProvider | undefined;
    
    try {
      // ✅ Add timeout protection (30 seconds)
      const result = await this.withTimeout(
        initPromise,
        30000,
        `Y.js initialization for ${docId.slice(-8)}`
      );
      doc = result.doc;
      provider = result.provider;
    
    // 🔧 CRITICAL FIX: Store initial base64 SIZE (decoded bytes) AND element count for size-drop detection
    let initialBase64Size: number | null = null;
    let initialElementCount: number | null = null;
    if (initialBase64) {
      try {
        // Decode to get actual Y.js state size (not base64 string length)
        const bin = atob(initialBase64);
        initialBase64Size = bin.length; // Store actual binary size in bytes
        
        // Also decode to get initial element count (more reliable than byte size)
        const tempDoc = new Y.Doc();
        const update = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) {
          update[i] = bin.charCodeAt(i);
        }
        Y.applyUpdateV2(tempDoc, update);
        const tempYxml = tempDoc.getXmlFragment('prosemirror');
        initialElementCount = tempYxml.length;
        console.warn(`📊 [INITIAL-STATE] ${docId.slice(-8)} - Initial content: ${initialElementCount} elements, ${bin.length} bytes`);
      } catch (e) {
        console.warn(`⚠️ [SIZE-TRACKING] ${docId.slice(-8)} - Could not decode initialBase64 for size tracking`);
      }
    }
    
    // Provider result (verbose logs removed)
    
    // 🔍 DEBUG: Check for existing provider listeners before adding (CONFLICT DETECTION)
    if (provider) {
      // ✅ Type-safe: Create const reference for use in callbacks
      const providerRef = provider;
      
      const statusListeners = (provider as any)._callbacks?.status?.length || 0;
      const syncListeners = (provider as any)._callbacks?.sync?.length || 0;
      // Adding docsCRDTService provider listeners (verbose logs removed)
      
      providerRef.on('status', ({ status }: { status: string }) => {
        const yxml = doc.getXmlFragment('prosemirror');
        log.log(`🔌 [STATUS_TRACE:${docId.slice(-8)}] Provider status: ${status}`, {
          elementCount: yxml.length,
          connected: providerRef.wsconnected,
          synced: providerRef.synced,
          roomName: providerRef.roomname,
          timestamp: new Date().toISOString()
        });
        
        // ✅ ROBUSTNESS: Auto-recovery on disconnection
        if (status === 'disconnected') {
          console.warn(`⚠️ [STATUS_TRACE:${docId.slice(-8)}] Disconnected with ${yxml.length} elements`);
          // Connection lost - will auto-reconnect (verbose logs removed)
          // Y.js WebSocket provider handles reconnection automatically
        } else if (status === 'connected') {
          log.log(`✅ [STATUS_TRACE:${docId.slice(-8)}] Connected! Client will now send its state to server`);
          log.log(`📤 [STATUS_TRACE:${docId.slice(-8)}] CLIENT STATE TO SEND:`, {
            elements: yxml.length,
            clientId: doc.clientID,
            roomName: providerRef.roomname
          });
        }
      });
      
      // 🚨 LISTEN FOR CORRUPTION NOTIFICATIONS from WebSocket server
      // @ts-ignore - WebSocket provider doesn't have types for custom messages
      // ✅ CORRUPTION DETECTION: Removed - Smart cache with version tracking handles staleness
      // Old size-drop detection was disabled in WebSocket server (too aggressive, false positives)
      // Version-based stale detection is more reliable and doesn't require page reloads
      
      // Keep listener for potential future server messages, but don't auto-reload
      providerRef.ws?.addEventListener('message', (event: MessageEvent) => {
        try {
          // Try to parse as JSON (server notifications are JSON)
          if (typeof event.data === 'string') {
            const message = JSON.parse(event.data);
            
            // Log any server messages for debugging, but don't auto-reload
            if (message.type) {
              log.log(`[WebSocket Message] ${docId.slice(-8)} received:`, message.type);
            }
          }
        } catch (e) {
          // Not JSON or not our message - ignore (Y.js uses binary messages)
        }
      });
      
      providerRef.on('sync', (isSynced: boolean) => {
        if (isSynced) {
          const yxml = doc.getXmlFragment('prosemirror');
          const currentSize = yxml.length;
          const expectedSize = this.expectedDocumentSizes.get(docId);
          
          log.log(`🔄 [SYNC_TRACE:${docId.slice(-8)}] === WEBSOCKET SYNC EVENT ===`, {
            isSynced,
            currentSize,
            expectedSize,
            delta: expectedSize ? (currentSize - expectedSize) : 'no-baseline',
            percentChange: expectedSize ? Math.round((currentSize / expectedSize) * 100) : 'n/a',
            providerConnected: providerRef.wsconnected,
            providerSynced: providerRef.synced,
            roomName: providerRef.roomname,
            timestamp: new Date().toISOString()
          });
          
          log.log('[sync] WebSocket synced', { docId: docId.slice(-8), elementCount: currentSize });
          
          // Detect significant content loss after WebSocket sync
          if (expectedSize && currentSize < expectedSize * 0.5) {
            console.error(`🚨 [SYNC] Content loss detected for ${docId.slice(-8)}! Expected ${expectedSize}, got ${currentSize}`);
            
            // Abort recovery if user has made local edits
            const lastEditTime = this.lastLocalEditTime.get(docId) || 0;
            const timeSinceLastEdit = Date.now() - lastEditTime;
            const userHasRecentEdits = timeSinceLastEdit < 10000;
            
            if (userHasRecentEdits) {
              console.error(`🛡️ [SYNC] Recovery aborted - user has recent edits`);
              if (!document.title.startsWith('⚠️ SYNC ISSUE')) {
                document.title = '⚠️ SYNC ISSUE - ' + document.title;
              }
              return;
            }
            
            console.error(`🔄 [SYNC] Attempting recovery...`);
            
            // 🔥 EMERGENCY RECOVERY: Fetch fresh content from backend and re-apply
            // ⚠️ CRITICAL: Must fetch from backend, NOT use initialBase64 closure variable
            // initialBase64 is stale (captured at initialization), backend has the latest
            (async () => {
              try {
                // Fetch fresh entity from backend
                log.log(`[AUTO-RECOVERY] Fetching fresh content from backend for ${docId.slice(-8)}...`);
                const freshEntity = await entityService.get(docId);
                const freshBase64 = freshEntity?.skeleton?.base64;
                
                if (!freshBase64 || freshBase64.length < 100) {
                  console.error(`❌ [AUTO-RECOVERY] Backend has no content to recover from!`);
                  console.error(`❌ [DATA-LOSS] Backend base64 length: ${freshBase64?.length || 0} bytes`);
                  console.error(`❌ [DATA-LOSS] This indicates PERMANENT data loss - backend state is also empty`);
                  console.error(`❌ [MANUAL-RECOVERY-REQUIRED] Check WebSocket server yjs_documents collection`);
                  return;
                }
                
                // Decode to verify size
                const bin = atob(freshBase64);
                log.log(`✅ [AUTO-RECOVERY] Fetched fresh backend content: ${bin.length} bytes, ${freshBase64.length} base64 chars`);
                
                // Apply fresh backend content
                doc.transact(() => {
                  const update = new Uint8Array(bin.length);
                  for (let i = 0; i < bin.length; i++) {
                    update[i] = bin.charCodeAt(i);
                  }
                  Y.applyUpdateV2(doc, update);
                }, 'websocket-content-loss-recovery');
                
                const recoveredSize = yxml.length;
                log.log(`✅ [AUTO-RECOVERY] Document recovered: ${recoveredSize} elements (was ${currentSize}, expected ${expectedSize})`);
                
                // Verify recovery was successful
                if (recoveredSize < expectedSize * 0.8) {
                  console.error(`⚠️ [AUTO-RECOVERY] Recovery incomplete: expected ${expectedSize}, got ${recoveredSize}`);
                  console.error(`⚠️ [AUTO-RECOVERY] Backend may have stale state too - check last save timestamp`);
                } else {
                  log.log(`✅ [AUTO-RECOVERY] Recovery successful! Document restored to ${recoveredSize} elements`);
                  
                  // Update expected size to match recovered size
                  this.expectedDocumentSizes.set(docId, recoveredSize);
                }
                
                // Force save to push correct state back to WebSocket server
                log.log(`[AUTO-RECOVERY] Force-saving to WebSocket server to prevent future content loss...`);
                await this.performValidatedSave(docId, yxml);
                log.log(`✅ [AUTO-RECOVERY] Force-save complete - WebSocket server now has correct state`);
              } catch (recoveryError) {
                console.error(`❌ [AUTO-RECOVERY] Failed to recover document:`, recoveryError);
                console.error(`❌ [MANUAL-RECOVERY-REQUIRED] Document may need manual recovery by administrator`);
                
                // Log full error details for debugging
                if (recoveryError instanceof Error) {
                  console.error(`❌ [AUTO-RECOVERY] Error details:`, {
                    message: recoveryError.message,
                    stack: recoveryError.stack
                  });
                }
              }
            })();
          }
          
          // ✅ SANITY CHECK: Only check for completely empty document (clear bug indicator)
          // This should NEVER happen - if it does, it's a critical WebSocket server bug
          const wasExpectingContent = expectedSize && expectedSize > 0;
          if (currentSize === 0 && wasExpectingContent) {
            console.error(`🚨🚨🚨 [WEBSOCKET-BUG] ${docId.slice(-8)} - Document is EMPTY after sync but we expected ${expectedSize} elements!`);
            console.error(`🚨 [WEBSOCKET-BUG] This indicates a critical bug in the WebSocket server`);
            console.error(`🚨 [WEBSOCKET-BUG] Provider details:`, {
              roomName: providerRef.roomname,
              connected: providerRef.wsconnected,
              synced: providerRef.synced,
              wsUrl: providerRef.url,
              wsReadyState: providerRef.ws?.readyState
            });
            
            // 🛡️ SAFETY CHECK: Abort recovery if user has made local edits
            const lastEditTime = this.lastLocalEditTime.get(docId) || 0;
            const timeSinceLastEdit = Date.now() - lastEditTime;
            const recentEditWindow = 10000; // 10 second safety window
            const userHasRecentEdits = timeSinceLastEdit < recentEditWindow;
            
            if (userHasRecentEdits) {
              console.error(`🛡️ [RECOVERY-ABORTED] ${docId.slice(-8)} - User has recent edits (${(timeSinceLastEdit/1000).toFixed(1)}s ago)`);
              console.error(`🛡️ [RECOVERY-ABORTED] Not applying recovery to preserve user's work`);
              console.error(`🛡️ [ACTION-REQUIRED] User should save and refresh page to resolve sync issues`);
              
              if (!document.title.startsWith('⚠️ SYNC ISSUE')) {
                document.title = '⚠️ SYNC ISSUE - ' + document.title;
              }
              return;
            }
            
            console.error(`🚨 [AUTO-RECOVERY] No recent user edits - safe to attempt recovery`);
            console.error(`🚨 [AUTO-RECOVERY] Fetching fresh backend content to recover document...`);
            
            // 🔥 EMERGENCY RECOVERY: Fetch fresh content from backend
            // ⚠️ CRITICAL: Must fetch from backend, NOT use initialBase64 closure variable
            (async () => {
              try {
                log.log(`[AUTO-RECOVERY] Fetching fresh content from backend for empty document ${docId.slice(-8)}...`);
                const freshEntity = await entityService.get(docId);
                const freshBase64 = freshEntity?.skeleton?.base64;
                
                if (!freshBase64 || freshBase64.length < 100) {
                  console.error(`❌ [AUTO-RECOVERY] Backend also has no content!`);
                  console.error(`❌ [DATA-LOSS] This is PERMANENT data loss - both WebSocket and backend are empty`);
                  console.error(`❌ [MANUAL-RECOVERY-REQUIRED] Check WebSocket server yjs_documents collection for backup`);
                  return;
                }
                
                // Apply fresh backend content
                const bin = atob(freshBase64);
                doc.transact(() => {
                  const update = new Uint8Array(bin.length);
                  for (let i = 0; i < bin.length; i++) {
                    update[i] = bin.charCodeAt(i);
                  }
                  Y.applyUpdateV2(doc, update);
                }, 'websocket-recovery');
                
                const recoveredSize = yxml.length;
                log.log(`✅ [AUTO-RECOVERY] Document recovered from empty: ${recoveredSize} elements`);
                
                // Update expected size
                this.expectedDocumentSizes.set(docId, recoveredSize);
                
                // Force save to WebSocket server
                log.log(`[AUTO-RECOVERY] Force-saving to WebSocket server...`);
                await this.performValidatedSave(docId, yxml);
                log.log(`✅ [AUTO-RECOVERY] Empty document recovery complete`);
              } catch (recoveryError) {
                console.error(`❌ [AUTO-RECOVERY] Failed to recover empty document:`, recoveryError);
                console.error(`❌ [MANUAL-RECOVERY-REQUIRED] Document needs manual recovery`);
              }
            })();
          }
          
          // ✅ NO SIZE CHECKS: Trust WebSocket sync completely
          // Size changes are NORMAL (edits, deletions, Y.js compression)
          // If sync is wrong, it's a WebSocket bug that needs fixing, not a workaround
        }
      });
      
      // ✅ ROBUSTNESS: Handle connection errors gracefully
      provider.on('connection-error', (error: any) => {
        console.warn(`⚠️ [CRDT-RECOVERY] Connection error for ${docId.slice(-8)}:`, error?.message || error);
      });
    }
    
    // 🛡️ CRITICAL: Add Y.Doc error handler to gracefully handle internal Y.js errors
    // This catches errors that occur during WebSocket message decoding
    // @ts-ignore - Y.Doc does support 'error' event but TypeScript types are incomplete
    doc.on('error', (error: Error) => {
      console.error(`🚨 [Y.DOC-ERROR] ${docId.slice(-8)} - Y.js internal error:`, error.message);
      console.error(`🚨 [Y.DOC-ERROR] Stack:`, error.stack);
      
      // Check if this is a decoding/corruption error
      const isDecodingError = error.message?.includes('Unexpected end of array') ||
                              error.message?.includes('decoding') ||
                              error.message?.includes('decoder') ||
                              error.message?.includes('corrupt') ||
                              error.message?.includes('Invalid');
      
      if (isDecodingError) {
        console.error(`🚨 [CORRUPTION] ${docId.slice(-8)} - Corrupted binary data detected from WebSocket`);
        console.error(`🚨 [CORRUPTION] This means the server sent invalid Y.js binary data`);
        
        // Mark document as corrupted to prevent save loops
        this.corruptedDocuments.add(docId);
        console.warn(`⚠️ [CORRUPTION] ${docId.slice(-8)} - Marked as corrupted, saves will be blocked`);
        
        // Disconnect provider to stop receiving more corrupted data
        if (provider) {
          try {
            console.warn(`🔌 [CORRUPTION] Disconnecting WebSocket provider for ${docId.slice(-8)}`);
            provider.disconnect();
            log.log(`✅ [CORRUPTION] Provider disconnected for ${docId.slice(-8)}`);
          } catch (disconnectError) {
            console.error(`❌ [CORRUPTION] Failed to disconnect provider:`, disconnectError);
          }
        }
        
        // Log recovery instructions
        console.warn(`📋 [RECOVERY-NEEDED] ${docId.slice(-8)} - Automatic recovery options:`);
        console.warn(`   1. Refresh the page (will attempt backend recovery)`);
        console.warn(`   2. Check browser console for document ID`);
        console.warn(`   3. Verify database integrity in both 'entities' and 'yjs_documents' collections`);
        console.warn(`   4. Contact support if issue persists`);
        
        // Attempt automatic recovery by fetching from backend
        console.warn(`🔄 [AUTO-RECOVERY] ${docId.slice(-8)} - Attempting to recover from EntityService...`);
        
        // Clear Y.Doc content and try to reload from backend
        setTimeout(async () => {
          try {
            console.warn(`🔄 [AUTO-RECOVERY] Fetching fresh document from backend for ${docId.slice(-8)}`);
            const freshEntity = await entityService.get(docId);
            
            if (freshEntity?.skeleton?.base64) {
              console.warn(`✅ [AUTO-RECOVERY] Found backend base64 (${freshEntity.skeleton.base64.length} chars)`);
              
              // Clear current document
              const yxml = doc.getXmlFragment('prosemirror');
              doc.transact(() => {
                while (yxml.length > 0) {
                  yxml.delete(0, 1);
                }
              }, 'corruption-recovery');
              
              console.warn(`🔄 [AUTO-RECOVERY] Cleared corrupted content, applying backend state...`);
              
              // Apply fresh backend state
              const bin = atob(freshEntity.skeleton.base64);
              const update = new Uint8Array(bin.length);
              for (let i = 0; i < bin.length; i++) {
                update[i] = bin.charCodeAt(i);
              }
              
              Y.applyUpdateV2(doc, update);
              
              const restoredSize = yxml.length;
              console.warn(`✅ [AUTO-RECOVERY] Document recovered! Restored ${restoredSize} elements`);
              
              // Unmark as corrupted if recovery successful
              if (restoredSize > 0) {
                this.corruptedDocuments.delete(docId);
                console.warn(`✅ [AUTO-RECOVERY] ${docId.slice(-8)} - Unmarked as corrupted, saves re-enabled`);
              }
              
            } else {
              console.error(`❌ [AUTO-RECOVERY] No base64 found in backend for ${docId.slice(-8)}`);
              console.error(`❌ [AUTO-RECOVERY] Manual recovery required - page refresh recommended`);
            }
            
          } catch (recoveryError) {
            console.error(`❌ [AUTO-RECOVERY] Recovery failed for ${docId.slice(-8)}:`, recoveryError);
            console.error(`❌ [AUTO-RECOVERY] User should refresh the page`);
          }
        }, 1000); // Wait 1 second to avoid hammering the backend
      }
    });
    
    // CRITICAL: Set up awareness IMMEDIATELY after provider creation
    if (provider) {
      debugLog('[DocsApp - CRDT] 🔍 MULTIPLAYER DEBUG: Provider created for doc:', {
        docId: docId.slice(-8),
        docKey,
        roomName: provider.roomname,
        websocketUrl: provider.url,
        connected: provider.wsconnected,
        synced: provider.synced,
        awarenessStates: provider.awareness?.getStates?.()?.size || 0
      });
      
      // REMOVED: WebSocket message validation (was causing performance issues)
      
      await this.setupUserAwareness(provider, docId, doc);
      // ✅ CRITICAL FIX: Reduced suppression from 1500ms to 300ms
      // 1500ms was too long - users could paste and refresh before save completes
      // 300ms is enough to prevent save during initial load, but allows fast saves after
      this.saveSuppressionUntil.set(docId, Date.now() + 300);
      log.log(`🔕 [SAVE_SUPPRESSION] Saves suppressed for ${docId.slice(-8)} until ${new Date(Date.now() + 300).toISOString()}`);
      log.log(`⏰ [SAVE_SUPPRESSION] Suppression will expire in 300ms, then edits will auto-save`);
    } else {
      log.warn('[DocsApp - CRDT] No provider available for doc:', docId);
      // Try again after a short delay
      setTimeout(async () => {
        const delayedProvider = this.crdt.getProvider(docKey);
        if (delayedProvider) {
          debugLog('[DocsApp - CRDT] 🔍 MULTIPLAYER DEBUG: Delayed provider found:', {
            docId: docId.slice(-8),
            docKey,
            roomName: delayedProvider.roomname,
            websocketUrl: delayedProvider.url,
            connected: delayedProvider.wsconnected,
            synced: delayedProvider.synced,
            awarenessStates: delayedProvider.awareness?.getStates?.()?.size || 0
          });
          // REMOVED: WebSocket message validation (was causing performance issues)
          await this.setupUserAwareness(delayedProvider, docId, doc);
          // ✅ CRITICAL FIX: Reduced suppression from 1500ms to 300ms
          this.saveSuppressionUntil.set(docId, Date.now() + 300);
          log.log(`🔕 [SAVE_SUPPRESSION] Saves suppressed for ${docId.slice(-8)} (delayed provider) until ${new Date(Date.now() + 300).toISOString()}`);
        }
      }, 100);
    }
    
    // Get or create Y.XmlFragment
    const yxml = doc.getXmlFragment('prosemirror');
    const wasAlreadyCached = this.yContentCache.has(docId);
    this.yContentCache.set(docId, yxml);
    
    debugLog('[DocsApp - CRDT] Y.XmlFragment access:', {
      docId: docId.slice(-8),
      wasAlreadyCached,
      yxmlLength: yxml.length,
      hasInitialContent: !!(initialBase64 || initialContent)
    });
    
    // ✅ FIX: Initialize based on document emptiness, not cache status
    const isDocumentEmpty = yxml.length === 0;
    let hasContentToLoad = !!(initialBase64 || initialContent);
    
    console.warn(`[DocsCRDT] 📊 Y.Doc state before initialization:`, {
      docId: docId.slice(-8),
      isDocumentEmpty,
      hasContentToLoad,
      wasAlreadyCached,
      yxmlLength: yxml.length
    });
    
    // 🛡️ DEFENSIVE: If document is empty but should have content, attempt recovery from backend
    // This is a last line of defense against the blank document bug
    if (isDocumentEmpty && !hasContentToLoad && docId) {
      // Skip recovery if we already know this document has corrupted base64
      if (this.corruptedDocuments.has(docId)) {
        console.error(`🚨 [RECOVERY-BLOCKED] ${docId.slice(-8)} - Previously detected corrupted base64, skipping recovery`);
        console.error(`🚨 [RECOVERY-BLOCKED] This document needs manual recovery - will not attempt automatic load`);
      } else {
        console.warn(`⚠️ [RECOVERY] Document ${docId.slice(-8)} is empty with no content to load, attempting backend recovery`);
        
        try {
          // Fetch fresh from backend as last resort
          console.warn(`🔄 [RECOVERY] Fetching fresh entity from backend for ${docId.slice(-8)}`);
          const freshEntity = await entityService.get(docId);
          
          if (freshEntity?.skeleton?.base64) {
            const recoveredBase64 = freshEntity.skeleton.base64;
            initialBase64 = recoveredBase64;
            hasContentToLoad = true;
            console.warn(`✅ [RECOVERY] Recovered Y.js base64 from backend (${recoveredBase64.length} bytes)`);
            console.warn(`✅ [RECOVERY] Document will be initialized with recovered content`);
            
            // ✅ SMART CACHE: Track backend version for stale detection
            if (freshEntity.skeleton.version) {
              this.docVersions.set(docId, freshEntity.skeleton.version);
            }
          } else if (freshEntity?.skeleton?.content) {
            const recoveredContent = freshEntity.skeleton.content;
            initialContent = recoveredContent;
            hasContentToLoad = true;
            console.warn(`✅ [RECOVERY] Recovered HTML/JSON content from backend (${recoveredContent.length} chars)`);
            console.warn(`✅ [RECOVERY] Document will be initialized from HTML/JSON`);
          } else {
            console.warn(`⚠️ [RECOVERY] Backend fetch succeeded but document is truly empty`);
            console.warn(`⚠️ [RECOVERY] This is either a new document or content was never saved`);
          }
        } catch (error) {
          console.error(`❌ [RECOVERY] Failed to recover content from backend:`, error);
          console.error(`❌ [RECOVERY] Document will load empty - may be new document or backend error`);
          // Continue with empty document - might be a new document
        }
      }
    }
    
    if (isDocumentEmpty && hasContentToLoad) {
      log.log(`🔧 [CRDT_TRACE:${crdtTraceId}] === INITIALIZING DOCUMENT CONTENT ===`);
      console.warn(`[DocsCRDT] 🔧 INITIALIZING DOCUMENT CONTENT for ${docId.slice(-8)}`);
      console.warn(`[DocsCRDT] 📊 Content sources available:`, {
        hasBase64: !!initialBase64,
        base64Length: (initialBase64 || '').length,
        hasHtmlContent: !!initialContent,
        htmlLength: (typeof initialContent === 'string' ? initialContent.length : 0),
        preferredSource: initialBase64 ? '🎯 Y.js binary (base64)' : '⚠️ HTML fallback',
        willLoadWelcomeImage: includeWelcomeImage,
        hasPageMargins: !!pageMargins
      });
      
      const initContentStartTime = Date.now();
      const initPromise = this.initializeDocumentContent(doc, yxml, docId, initialContent, initialBase64, includeWelcomeImage, pageMargins);
      this.initializationPromises.set(docId, initPromise.then(() => ({ doc, provider })));
      
      log.log(`⏳ [CRDT_TRACE:${crdtTraceId}] Awaiting content initialization...`);
      
      // ✅ CRITICAL FIX: MUST await initialization before returning doc
      // Without this await, doc_write gets a Y.Doc with uninitialized Y.XML structure
      // causing "Invalid access: Add Yjs type to a document before reading data" errors
      await initPromise;
      
      const initContentDuration = Date.now() - initContentStartTime;
      log.log(`✅ [CRDT_TRACE:${crdtTraceId}] Content initialization complete (${initContentDuration}ms)`);
      
      // Clean up promise after completion
      const finalSize = yxml.length;
      console.warn(`[DocsCRDT] ✅ Initialization completed for ${docId.slice(-8)}, final size: ${finalSize} elements`);
      this.initializationPromises.delete(docId);
    } else if (!isDocumentEmpty) {
      console.warn(`[DocsCRDT] ℹ️ Document ${docId.slice(-8)} already has content (${yxml.length} elements), skipping initialization`);
    } else {
      console.warn(`[DocsCRDT] ℹ️ Document ${docId.slice(-8)} is empty and no content to load - new document`);
    }

    // ✅ CRITICAL FIX: ALWAYS connect WebSocket provider AFTER backend content is loaded
    // This prevents stale server state from overwriting freshly loaded backend content
    // Backend is the source of truth - WebSocket is only for real-time sync
    log.log(`🔌 [CRDT_TRACE:${crdtTraceId}] === CONNECTING PROVIDER ===`, {
      yxmlLength: yxml.length,
      wasRecreatingFromStale: isRecreatingFromStale,
      docKey,
      roomName: `doc:${docId}`,
      expectedDocSizeBeforeConnect: this.expectedDocumentSizes.get(docId),
      clientHasContent: yxml.length > 0,
      clientContentSummary: yxml.length > 0 ? `${yxml.length} elements` : 'empty'
    });
    console.warn(`[TEST] 🔌 WEBSOCKET CONNECTING - Backend content loaded (${yxml.length} elements)`);
    console.warn(`[TEST] 💾 CLIENT STATE BEFORE SYNC:`, {
      docId: docId.slice(-8),
      elements: yxml.length,
      contentPreview: yxml.toString().substring(0, 200),
      expectedSize: this.expectedDocumentSizes.get(docId),
      timestamp: new Date().toISOString()
    });
    console.warn(`[TEST] ⏱️ Connecting WebSocket at: ${new Date().toISOString()}`);
    
    // 🔥 CRITICAL: Add one-time sync listener to see what server sends us
    if (provider) {
      provider.once('sync', (isSynced: boolean) => {
        const yxmlAfterSync = doc.getXmlFragment('prosemirror');
        console.error(`🔥 [SYNC-CRITICAL] FIRST SYNC EVENT - What did server send us?`, {
          isSynced,
          clientBeforeSync: yxml.length,
          clientAfterSync: yxmlAfterSync.length,
          contentChange: yxml.length - yxmlAfterSync.length,
          contentBefore: yxml.toString().substring(0, 100),
          contentAfter: yxmlAfterSync.toString().substring(0, 100),
          serverOverwriteDetected: yxml.length > 0 && yxmlAfterSync.length === 0,
          timestamp: new Date().toISOString()
        });
        
        if (yxml.length > 0 && yxmlAfterSync.length === 0) {
          console.error(`❌❌❌ [SYNC-CRITICAL] SERVER OVERWROTE OUR CONTENT WITH EMPTY STATE!`);
          console.error(`❌ This means the WebSocket server has stale/empty state`);
          console.error(`❌ Backend had ${yxml.length} elements, server synced 0 elements`);
        } else if (yxml.length > 0 && yxmlAfterSync.length < yxml.length) {
          console.error(`⚠️ [SYNC-CRITICAL] SERVER PARTIALLY OVERWROTE CONTENT`, {
            lost: yxml.length - yxmlAfterSync.length,
            kept: yxmlAfterSync.length
          });
        } else if (yxmlAfterSync.length === yxml.length) {
          log.log(`✅ [SYNC-CRITICAL] Sync completed without content loss`);
        }
      });
    }
    
    // Connect WebSocket AFTER backend content is applied (backend is source of truth)
    this.crdt.connectProvider(docKey);
    if (isRecreatingFromStale) {
      this.recreatingFromStaleVersion.delete(docId);
    }
    log.log(`✅ [CRDT_TRACE:${crdtTraceId}] Provider connected successfully`);
    console.warn(`[TEST] ✅ WEBSOCKET CONNECTED - Real-time sync enabled`);
    console.warn(`[TEST] ✅ Initialization sequence: Backend → Y.Doc → WebSocket (CORRECT ORDER)`);
    
    console.warn(`[TEST] ========================================`);
    log.log('[getYDocForDoc] WebSocket connected', { docId: docId.slice(-8), elements: yxml.length });


    // Set document name if provided (only thing we keep from legacy system)
    if (initialName) {
      const nameText = doc.getText('name');
      if (nameText.length === 0) {
        nameText.insert(0, initialName);
        debugLog('[DocsApp - CRDT] Set document name:', initialName);
      }
    }

    debugLog('[DocsApp - CRDT] Document ready:', { 
      wsconnected: provider?.wsconnected, 
      synced: provider?.synced,
      hasContent: yxml.length > 0,
      hasInitialContent: !!(initialBase64 || initialContent),
      wasAlreadyCached,
      docId: docId.slice(-8)
    });

    // 🚨 MULTI-TAB PROTECTION: Mark that this tab has authoritative content from backend
    // This prevents other tabs from overwriting our freshly loaded content during initialization
    if (yxml.length > 0 || initialBase64) {
      (doc as any).__hasAuthoritativeContent = true;
      (doc as any).__contentLoadedAt = Date.now();
      log.log(`🛡️ [MULTI-TAB-PROTECTION] ${docId.slice(-8)} - Tab marked as having authoritative content:`, {
        elements: yxml.length,
        hasBase64: !!initialBase64,
        timestamp: new Date().toISOString()
      });
    }
    
    // Set up an observer for Y.js document changes to auto-persist (if not already set up)
    if (!this.activeDocObservers.has(docId)) {
      log.log(`🔔 [SAVE_OBSERVER] Setting up document observer for ${docId.slice(-8)}`);
      this.setupDocObserver(docId, doc);
      log.log(`✅ [SAVE_OBSERVER] Document observer active for ${docId.slice(-8)} - will trigger saves on edits`);
      debugLog('[DocsApp - CRDT] Set up document observer for auto-persistence:', docId.slice(-8));
    } else {
      log.log(`✅ [SAVE_OBSERVER] Document observer already exists for ${docId.slice(-8)}`);
    }
    
    // 🔥 SIMPLE: Just force save on reconnect (lightweight, effective)
    if (provider) {
      this.setupSimpleReconnectHandler(docId, provider, doc);
    }

      const crdtDuration = Date.now() - crdtStartTime;
      
      log.log(`🟢 [CRDT_TRACE:${crdtTraceId}] === CRDT.GETYDOCFORDOC COMPLETE (${crdtDuration}ms) ===`, {
        docId: docId.slice(-8),
        contentElements: yxml.length,
        expectedSize: this.expectedDocumentSizes.get(docId),
        wsConnected: provider?.wsconnected || false,
        wsSynced: provider?.synced || false,
        hasInitialContent: !!(initialBase64 || initialContent),
        wasAlreadyCached,
        isNewDocument: yxml.length === 0 && !initialBase64 && !initialContent,
        duration: crdtDuration
      });
      
      // ✅ CRITICAL FIX: Initialize docVersions to prevent stale detection on immediate re-access
      // For new documents created via docs_create with initialContent, backend already has version 1
      // Set docVersions optimistically to avoid unnecessary recreation when editor loads
      if (!wasAlreadyCached && !this.docVersions.has(docId)) {
        this.docVersions.set(docId, 1);
        log.log(`🔧 [CRDT_TRACE:${crdtTraceId}] Set initial version tracker to 1 (prevents stale detection)`);
      }

      return {doc, provider};
      
    } catch (error) {
      console.error(`❌ [CRDT] Initialization failed for ${docId.slice(-8)}:`, error);
      
      // Clean up partial state
      const docKey = this.getDocKey(docId, entityType);
      try {
        this.crdt.disconnect(docKey);
        this.initializationPromises.delete(docId);
        this.connectionAttempts.delete(docId);
      } catch (cleanupError) {
        console.error(`[CRDT] Cleanup error for ${docId.slice(-8)}:`, cleanupError);
      }
      
      throw error; // Re-throw to caller
      
    } finally {
      // ✅ CRITICAL: Always clean up lock, success or failure
      log.log(`🔓 [CRDT_TRACE:${crdtTraceId}] Releasing global lock`);
      globalInitializationLock.delete(docId);
      
      // 📊 Track lock duration for performance monitoring
      const lockDuration = Date.now() - (this.lockMetrics.get(docId) || 0);
      this.lockMetrics.delete(docId);
      
      if (lockDuration > 5000) {
        console.warn(`⚠️ [CRDT-PERF] Slow initialization: ${docId.slice(-8)} took ${lockDuration}ms`);
      }
    }
  }

  // Setup an observer for document changes to automatically persist to DB
  private setupDocObserver(docId: string, doc: Y.Doc) {
    // Clean up any existing observer
    const existingCleanup = this.activeDocObservers.get(docId);
    if (existingCleanup) {
      existingCleanup();
      this.activeDocObservers.delete(docId);
    }

    // Get Y.XmlFragment once for use in both setup and observer
    const yxml = doc.getXmlFragment('prosemirror');
    
    log.log(`🔔 [SAVE_OBSERVER] Setting up observer for ${docId.slice(-8)}:`, {
      initialContentLength: yxml.length
    });

    // 🔥 CRITICAL INSIGHT: At Y.Doc level, WebSocket updates have origin = WebsocketProvider object
    // We need to check if the origin is the WebsocketProvider instance!

    // Create a new observer - SIMPLIFIED like notes service
    const observer = (update: Uint8Array, origin: any) => {
      // 🔥 CRITICAL FIX: Detect remote vs local updates
      // At Y.Doc level:
      // - Remote updates (from WebSocket): origin = WebsocketProvider object
      // - Local updates (from ProseMirror/app): origin = null or something else
      const isWebsocketProvider = origin && 
                                  (origin.constructor?.name === 'WebsocketProvider' ||
                                   origin instanceof WebsocketProvider ||
                                   (origin.ws && origin.awareness));  // Has WebSocket properties
      
      const isRemoteUpdate = isWebsocketProvider;
      
      // 🔥 CRITICAL DIAGNOSTIC: Get state BEFORE and AFTER for comparison
      const yxmlStateBefore = doc.getXmlFragment('prosemirror').length;
      const yxmlTextBefore = doc.getXmlFragment('prosemirror').toString().substring(0, 100);
      
      // 🔥 DEBUG: Log to verify
      log.log(`🔔 [OBSERVER-ORIGIN-DEBUG] ${docId.slice(-8)} - Update:`, {
        updateSize: update.length,
        origin: origin?.constructor?.name || origin,
        originType: typeof origin,
        hasWs: !!origin?.ws,
        hasAwareness: !!origin?.awareness,
        isWebsocketProvider,
        isRemoteUpdate,
        yxmlLength: yxmlStateBefore,
        yxmlTextPreview: yxmlTextBefore,
        decision: isRemoteUpdate ? '⏭️ SKIP (WebSocket remote)' : '💾 SAVE (local edit)',
        timestamp: new Date().toISOString()
      });
      
      if (isRemoteUpdate) {
        log.log(`⏭️ [OBSERVER] ${docId.slice(-8)} - SKIPPED: Remote update (origin=WebsocketProvider)`);
        log.log(`⏭️ [OBSERVER] Document state after remote update:`, {
          elements: doc.getXmlFragment('prosemirror').length,
          textPreview: doc.getXmlFragment('prosemirror').toString().substring(0, 100)
        });
        log.log(`⏭️ [OBSERVER] ${docId.slice(-8)} - Skipped: Remote Y.js update from WebSocket`);
        return;
      }
      
      log.log(`✅ [OBSERVER] ${docId.slice(-8)} - LOCAL edit, triggering save!`);
      
      log.log(`🔔 [OBSERVER] ${docId.slice(-8)} - Update received:`, {
        updateSize: update?.length || 0,
        origin: origin?.constructor?.name || origin || 'null',
        currentElements: yxml.length
      });
      
      // Basic validation
      if (!update || update.length === 0) {
        log.log(`⏭️ [OBSERVER] ${docId.slice(-8)} - Skipped: empty update`);
        return;
      }
      
      // Check save suppression window (300ms after load)
      const now = Date.now();
      const suppressUntil = this.saveSuppressionUntil.get(docId) || 0;
      
      if (now < suppressUntil) {
        log.log(`⏭️ [OBSERVER] ${docId.slice(-8)} - Skipped: save suppressed (${suppressUntil - now}ms remaining)`);
        return;
      }
      
      // Track local edits
      this.lastLocalEditTime.set(docId, Date.now());
      
      log.log(`✅ [OBSERVER] ${docId.slice(-8)} - LOCAL edit, triggering save!`);
      
      // Trigger save - simple!
      this.triggerDebouncedSave(docId, yxml);
    };

    // Enhanced observer with update validation
    const enhancedObserver = (update: Uint8Array, origin: any) => {
      // Validate Y.js update before processing
      if (!update || update.length === 0) {
        return;
      }
      observer(update, origin);
    };
    
    // ✅ CRITICAL DEBUG: Monitor ALL Y.js changes including deletions
    doc.on('update', (update: Uint8Array, origin: any, ydoc: Y.Doc) => {
      // Call original observer
      enhancedObserver(update, origin);
      
      // 🔍 DELETION DETECTION: Track if content unexpectedly disappears
      // Only warn if we had content and now it's gone
      const currentLength = yxml.length;
      const hadContentBefore = currentLength > 1;  // More than just initial paragraph
      
      // Store previous length for comparison
      if (!this.previousLengths) this.previousLengths = new Map();
      const prevLength = this.previousLengths.get(docId) || 0;
      this.previousLengths.set(docId, currentLength);
      
      // Only warn if elements DECREASED significantly (lost more than 1 element)
      if (prevLength > 0 && currentLength < prevLength - 1) {
        console.warn(`⚠️ [DELETION-DETECTED] ${docId.slice(-8)} - Elements decreased unexpectedly!`, {
          previousLength: prevLength,
          currentLength,
          delta: prevLength - currentLength,
          origin: origin?.constructor?.name || origin,
          updateSize: update.byteLength,
          timestamp: new Date().toISOString()
        });
      }
    });
    
    this.activeDocObservers.set(docId, () => {
      doc.off('update', enhancedObserver);
    });
  }
  
  // 🔥 SIMPLIFIED: Lightweight reconnection handler (replaces heavy monitoring)
  private setupSimpleReconnectHandler(docId: string, provider: WebsocketProvider, doc: Y.Doc) {
    // Track if this is the initial connection (don't save on first connect)
    let isInitialConnect = true;
    
    // Simple: Just force save when WebSocket REconnects (not initial connect)
    // This ensures backend has latest state after any disconnection
    provider.on('status', ({ status }: { status: string }) => {
      if (status === 'connected') {
        // 🔥 CRITICAL FIX: Don't save on initial connection
        // When page first loads, we just loaded content from backend
        // Saving immediately would trigger unnecessary version conflicts
        if (isInitialConnect) {
          log.log(`✅ [RECONNECT] ${docId.slice(-8)} - Initial connection established, skipping save (just loaded from backend)`);
          isInitialConnect = false;
          return;  // ← Skip save on first connect
        }
        
        // 🔥 THROTTLE: Only save on REconnections, and throttle to prevent spam
        const now = Date.now();
        const lastSave = this.lastReconnectSaveTime.get(docId) || 0;
        const timeSinceLastSave = now - lastSave;
        
        log.log(`✅ [RECONNECT] ${docId.slice(-8)} - REconnected (last save ${Math.floor(timeSinceLastSave/1000)}s ago)`);
        
        // Only save once every 30 seconds on reconnect (prevents spam)
        if (timeSinceLastSave > 30000) {
          log.log(`💾 [RECONNECT] Forcing save after REconnection (throttled: 30s)`);
          
          const yxml = doc.getXmlFragment('prosemirror');
          if (yxml && yxml.length > 0) {
            this.lastReconnectSaveTime.set(docId, now);
            this.triggerDebouncedSave(docId, yxml);
          }
        } else {
          log.log(`⏭️ [RECONNECT] Skipping save (too soon after last reconnect save: ${Math.floor(timeSinceLastSave/1000)}s)`);
        }
      } else if (status === 'disconnected') {
        console.warn(`⚠️ [RECONNECT] ${docId.slice(-8)} - WebSocket disconnected (will auto-reconnect)`);
        log.log(`⚠️ [RECONNECT] ${docId.slice(-8)} - Disconnected, y-websocket will auto-reconnect`);
      }
    });
    
    log.log(`✅ [RECONNECT] Simple reconnect handler active for ${docId.slice(-8)}`);
  }

  getContent(docId: string): Y.XmlFragment | null {
    return this.yContentCache.get(docId) || null;
  }

  getProvider(docId: string, entityType?: string): WebsocketProvider | undefined {
    // Use cached entity type if not provided
    const type = entityType || this.entityTypes.get(docId);
    const docKey = this.getDocKey(docId, type);
    return this.crdt.getProvider(docKey);
  }

  async observeDoc(docId: string, callback: (update: DocUpdate) => void) {
    const { doc } = await this.getYDocForDoc(docId);
    
    // Wait for Prosemirror to create the Y.Text type
    const waitForContent = () => {
      return new Promise<Y.Text>((resolve) => {
        if (doc.share.has('content')) {
          resolve(doc.getText('content'));
        } else {
          const observer = () => {
            if (doc.share.has('content')) {
              doc.off('afterTransaction', observer);
              resolve(doc.getText('content'));
            }
          };
          doc.on('afterTransaction', observer);
        }
      });
    };

    const text = await waitForContent();
    const observer = () => {
      this.yContentCache.set(docId, doc.getXmlFragment('prosemirror'));
      // 🚨 CRITICAL FIX: DO NOT use text.toString() - Y.Text could contain formatting contamination!
      let safeContent = '';
      try {
        const anyText = text as any;
        if (anyText._content && typeof anyText._content === 'string') {
          safeContent = anyText._content;
        } else {
          console.warn('[docsCRDTService] 🚨 Using dangerous toString() in observer callback!');
          const contaminated = text.toString();
          safeContent = contaminated
            .replace(/<fontFamily[^>]*>.*?<\/fontFamily>/g, '')
            .replace(/<[^>]*>/g, '')
            .replace(/fontFamily/g, '');
        }
      } catch (error) {
        console.error('[docsCRDTService] CRITICAL: Cannot extract text safely in observer:', error);
        safeContent = '';
      }
      callback({ content: safeContent });
    };

    doc.on('update', observer);
    // await this.crdt.connect(docId, doc);

    return () => {
      doc.off('update', observer);
      this.crdt.disconnect(docId);
    };
  }

  cleanup(docId: string) {
    // Remove document observer if exists
    const cleanup = this.activeDocObservers.get(docId);
    if (cleanup) {
      cleanup();
      this.activeDocObservers.delete(docId);
    }
    
    // Flush and cleanup per-document debounce to ensure pending saves complete
    const debouncedFn = this.perDocumentDebouncedSaves.get(docId);
    if (debouncedFn) {
      try {
        debouncedFn.flush();
        debouncedFn.cancel();
        this.perDocumentDebouncedSaves.delete(docId);
      } catch (e) {
        // Ignore cleanup errors
      }
    }
    
    // Clean up awareness event listeners if they exist
    const entityType = this.entityTypes.get(docId);
    const docKey = this.getDocKey(docId, entityType);
    const provider = this.crdt.getProvider(docKey);
    if (provider && (provider as any).__awarenessCleanup) {
      try {
        (provider as any).__awarenessCleanup();
        delete (provider as any).__awarenessCleanup;
        debugLog(`[docsCRDT] Cleaned up awareness listeners for doc ${docId}`);
      } catch (error) {
        log.warn('[docsCRDT] Error cleaning up awareness listeners:', error);
      }
    }
    
    // Clear local cache but DON'T disconnect the provider
    // This allows multiplayer to continue working across component remounts
    this.yContentCache.delete(docId);
    
    // 🔥 NEW: Clean up reconnect throttle tracking
    this.lastReconnectSaveTime.delete(docId);
    
    debugLog(`[docsCRDT] Cleaned up local state for doc ${docId} (keeping provider alive for multiplayer)`);
    
    // REMOVED: this.crdt.disconnect(key) - this was breaking multiplayer
    // The provider should stay connected to maintain multiplayer functionality
  }

  // New method for complete cleanup (only use when document is permanently closed)
  completeCleanup(docId: string) {
    // First do regular cleanup
    this.cleanup(docId);
    
    // Then disconnect the provider completely
    try {
      const entityType = this.entityTypes.get(docId);
      const key = this.getDocKey(docId, entityType);
      this.crdt.disconnect(key);
      
      // Clean up cached entity type
      this.entityTypes.delete(docId);
      debugLog(`[docsCRDT] Completely disconnected CRDT provider for ${key}`);
    } catch (error) {
      log.error('[docsCRDT] Failed to disconnect CRDT provider:', error);
    }
  }

  // Force an immediate save - use this when you need to ensure content is saved right away
  async forceSave(docId: string, content: string, cleanAttributes = true): Promise<void> {
    try {
      // Clean the content before saving to remove any pending change attributes
      const cleanedContent = this.cleanChangeAttributes(content, !cleanAttributes);
      debugLog(`[docsCRDT] Forcing immediate save to DB for doc ${docId}`);
      await entityService.mergeUpdate(docId, {
        skeleton: {
          '@type': 'Doc',
          content: cleanedContent
        }
      });
      debugLog(`[docsCRDT] Successfully saved content to DB for doc ${docId}`);
    } catch (error) {
      log.error(`[docsCRDT] Failed to save content to DB for doc ${docId}:`, error);
      throw error;
    }
  }

  async updateDocContent(docId: string, content: string) {
    const docResult = await this.getYDocForDoc(docId);
    if (!docResult.doc) {
      log.error('[DocsCRDTService] No doc found for:', docId);
      return;
    }

    // Let Prosemirror handle the Y.Text updates
    // Only save to backend
    this.triggerDebouncedSave(docId, docResult.doc.getXmlFragment('prosemirror'));
  }

  saveToDB(docId: string, content: XmlFragment) {
    log.log(`[docsCRDT] saveToDB called for doc ${docId} (${content.length} chars)`);
    this.triggerDebouncedSave(docId, content);
  }

  // Explicit method to clean and save when accepting/rejecting changes
  async cleanAndSave(docId: string, yxml: Y.XmlFragment): Promise<void> {
    try {
      log.log(`[docsCRDT] cleanAndSave - explicitly cleaning change attributes for doc ${docId}`);
      
      // Convert Y.js to ProseMirror doc with error handling
      let doc: any;
      let meta: any;
      try {
        const result = initProseMirrorDoc(yxml, schema);
        doc = result.doc;
        meta = result.meta;
      } catch (syncError) {
        log.error(`[docsCRDT] Failed to convert Y.js to ProseMirror for doc ${docId}:`, syncError);
        
        // 🚨 CRITICAL: DO NOT USE FALLBACK DOCUMENT CREATION
        // Creating fallback documents can mask underlying YJS corruption issues
        // and lead to data loss or inconsistent document states
        
        throw new Error(`Critical YJS to ProseMirror conversion failure for document ${docId}: ${syncError instanceof Error ? syncError.message : 'Unknown error'}. This indicates document corruption and must be investigated.`);
        
        // ❌ REMOVED: Fallback document creation
        // Creating empty fallback documents masks real issues and can cause data loss
      }
      
      const serializer = DOMSerializer.fromSchema(schema);
      const fragment = serializer.serializeFragment(doc.content);
      
      // 🎯 UNICODE NATIVE: Use XMLSerializer instead of innerHTML to preserve Unicode characters
      // innerHTML automatically converts Unicode (—, ", ') to HTML entities (&mdash;, &quot;, &#39;)
      // XMLSerializer preserves the original Unicode characters
      const xmlSerializer = new XMLSerializer();
      const html = Array.from(fragment.childNodes)
        .map(node => xmlSerializer.serializeToString(node))
        .join('');
      
      // Force clean change attributes  
      const cleanedContent = this.cleanChangeAttributes(html, false);
      
      let base64 = '';
      if (yxml.doc) {
        const update = Y.encodeStateAsUpdateV2(yxml.doc); // 🔧 V2 API consistency
        // ✅ OPTIMIZED: Use array.join() instead of string concatenation for better performance
        const uint8Array = new Uint8Array(update);
        const binaryChunks: string[] = [];
        const chunkSize = 8192; // Process in 8KB chunks to avoid stack overflow
        
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
          const chunk = uint8Array.slice(i, i + chunkSize);
          binaryChunks.push(String.fromCharCode(...chunk));
        }
        const binaryString = binaryChunks.join('');
        
        base64 = btoa(binaryString);
      }
      
      const skeletonPayload: Record<string, unknown> = {
        '@type': 'Doc',
        content: cleanedContent
      };
      if (!SERVER_IS_DURABILITY_BOUNDARY) {
        skeletonPayload.base64 = base64;
      }
      await entityService.mergeUpdate(docId, {
        skeleton: skeletonPayload
      });
      
      log.log(`[docsCRDT] Successfully cleaned and saved doc ${docId}`);
    } catch (error) {
      log.error(`[docsCRDT] Failed to clean and save doc ${docId}:`, error);
      throw error;
    }
  }

  async disconnect() {
    // Flush all per-document debounces to ensure pending saves complete
    for (const [, debouncedFn] of this.perDocumentDebouncedSaves.entries()) {
      try {
        debouncedFn.flush();
        debouncedFn.cancel();
      } catch (e) {
        // Ignore flush errors during disconnect
      }
    }
    this.perDocumentDebouncedSaves.clear();
    
    // Clear all document observers
    for (const [docId, cleanup] of this.activeDocObservers.entries()) {
      cleanup();
    }
    this.activeDocObservers.clear();
    
    this.crdt.cleanup();
  }

  // Clean change attributes from content before persisting to database
  // Only clean attributes when explicitly requested (e.g., when accepting/rejecting changes)
  private cleanChangeAttributes(content: string, preserveChangeAttributes = true): string {
    if (!content) return content;
    
    // Add detailed logging to debug attribute preservation
    log.log('[cleanChangeAttributes] called:', {
      contentLength: content.length,
      preserveChangeAttributes,
      contentSample: content.substring(0, 200)
    });
    
    // If preserveChangeAttributes is true, don't clean them - this allows pending changes to persist
    if (preserveChangeAttributes) {
      // Check if there are any active change attributes in the content
      const hasChangeAttributes = /(?:data-revision-id|revision_id|data-change-status|change_status)="[^"]*"/.test(content);
      
      log.log('[cleanChangeAttributes] Checking for change attributes:', {
        hasChangeAttributes,
        regex: '/(?:data-revision-id|revision_id|data-change-status|change_status)="[^"]*"/',
        foundMatches: content.match(/(?:data-revision-id|revision_id|data-change-status|change_status)="[^"]*"/g) || []
      });
      
      if (hasChangeAttributes) {
        log.log('[cleanChangeAttributes] PRESERVING change attributes in content for pending changes');
        return content; // Return content unchanged to preserve change tracking
      } else {
        log.log('[cleanChangeAttributes] No change attributes found to preserve');
      }
    }
    
    try {
      // Remove revision_id and change_status attributes from all elements
      let cleaned = content;
      
      // Remove data-revision-id attributes
      cleaned = cleaned.replace(/\s*data-revision-id="[^"]*"/g, '');
      
      // Remove data-change-status attributes  
      cleaned = cleaned.replace(/\s*data-change-status="[^"]*"/g, '');
      
      // Remove revision_id attributes (direct XML attributes)
      cleaned = cleaned.replace(/\s*revision_id="[^"]*"/g, '');
      
      // Remove change_status attributes (direct XML attributes)
      cleaned = cleaned.replace(/\s*change_status="[^"]*"/g, '');
      
      // Remove batch-related attributes
      cleaned = cleaned.replace(/\s*batch_description="[^"]*"/g, '');
      cleaned = cleaned.replace(/\s*batch_index="[^"]*"/g, '');
      cleaned = cleaned.replace(/\s*batch_total="[^"]*"/g, '');
      
      // Clean up any double spaces that might result from attribute removal
      cleaned = cleaned.replace(/\s+/g, ' ');
      
      // Reduced logging frequency to prevent spam
      if (Math.random() < 0.1) {
        log.log('[cleanChangeAttributes] Cleaned change attributes from content:', {
          originalLength: content.length,
          cleanedLength: cleaned.length,
          attributesRemoved: content.length - cleaned.length
        });
      }
      
      return cleaned;
    } catch (error) {
      log.error('[docsCRDT] Error cleaning change attributes:', error);
      return content; // Return original if cleaning fails
    }
  }

  /**
   * Get the CRDT room key for a document
   * ✅ CRITICAL: Use correct prefix based on entity type!
   * - 'doc:' for Document entities → WebSocket loads from docPersistence
   * - 'note:' for Note entities → WebSocket loads from notePersistence
   * Without correct prefix, WebSocket creates EMPTY room and wipes client content!
   */
  private getDocKey(docId: string, entityType?: string): string {
    // Check if entity type is Note (from method parameter or cached entity)
    if (entityType === 'Note' || entityType === 'Content.Note') {
      const key = `note:${docId}`;
      console.warn(`🔑 [KEY-GEN] Using 'note:' prefix for Note entity: ${docId.slice(-8)} → ${key.slice(0, 20)}...`);
      return key;
    }
    
    // Default to 'doc:' for Document entities
    const key = `doc:${docId}`;
    console.warn(`🔑 [KEY-GEN] Using 'doc:' prefix for Document entity: ${docId.slice(-8)} → ${key.slice(0, 20)}...`);
    return key;
  }

  // Public method for testing - exposes provider access
  getProviderForTesting(docKey: string) {
    return this.crdt.getProvider(docKey);
  }

  // Public method to reset a corrupted document
  resetDocument(docId: string) {
    log.log('🔧 Resetting corrupted Y.js document:', docId);
    
    // Clear from cache
    this.yContentCache.delete(docId);
    
    // Clear any active observers
    const existingCleanup = this.activeDocObservers.get(docId);
    if (existingCleanup) {
      existingCleanup();
      this.activeDocObservers.delete(docId);
    }
    
    // Get the Y.js document and reset it
    const entityType = this.entityTypes.get(docId);
    const docKey = this.getDocKey(docId, entityType);
    const doc = this.crdt.getYDoc(docKey);
    
    // Clean up cached entity type
    this.entityTypes.delete(docId);
    if (doc) {
      const yxml = doc.getXmlFragment('prosemirror');
      yxml.delete(0, yxml.length); // Clear all content
      log.log('✅ Cleared Y.js document content');
      return true;
    } else {
      log.log('❌ No Y.js document found');
      return false;
    }
  }

  // REMOVED: setupWebSocketMessageValidation and verifyConnectionHealth methods
  // These were causing performance issues and 500 req/sec WebSocket spam

  /**
   * 🔧 NEW: Validate server sync health and trigger recovery if needed
   */
  private validateServerSyncHealth = (docId: string, yxml: Y.XmlFragment) => {
    const entityType = this.entityTypes.get(docId);
    const provider = this.crdt.getProvider(this.getDocKey(docId, entityType));
    if (!provider) return;

    const frontendElements = yxml.length;
    const isConnected = provider.wsconnected;
    const isSynced = provider.synced;
    
    // State validation logic
    const stateInfo = {
      docId: docId.slice(-8),
      frontendElements,
      isConnected,
      isSynced,
      timestamp: new Date().toISOString()
    };
    
    // Validating sync health (verbose logs removed)
    
    // 🚨 CRITICAL: Detect potential server sync failure
    if (isConnected && isSynced && frontendElements > 10) {
      // TODO: Add server-side element count comparison here when available
      // For now, just log the state for monitoring
      // Frontend elements count (verbose logs removed)
    }
    
    if (isConnected && !isSynced) {
      console.warn(`⚠️ [SYNC-WARNING] ${docId.slice(-8)} - Connected but not synced with ${frontendElements} elements`);
    }
    
    if (!isConnected) {
      console.error(`🚨 [SYNC-ERROR] ${docId.slice(-8)} - Not connected! ${frontendElements} elements at risk`);
      
      // Force EntityService save when connection is broken
      this.triggerDebouncedSave(docId, yxml);
    }
  };

  /**
   * Initialize document content in a race-condition-safe way
   */
  private async initializeDocumentContent(
    doc: Y.Doc, 
    yxml: Y.XmlFragment, 
    docId: string, 
    initialContent?: string | any, // Support both string (HTML/Markdown) and ProseMirror JSON
    initialBase64?: string, 
    includeWelcomeImage?: boolean,
    pageMargins?: { top: string; right: string; bottom: string; left: string }
  ): Promise<void> {
    try {
      // ✅ CRITICAL: ALWAYS apply backend base64 if provided (source of truth)
      // Note: With our fixes, yxml should always be empty here (provider disconnected during init)
      // This is defensive programming - if somehow yxml has content, we'll see it in logs
      if (initialBase64 && typeof initialBase64 === 'string') {
        const yxmlLengthBefore = yxml.length;
        
        // ✅ DEFENSIVE: Log if yxml unexpectedly has content (should never happen with our fixes)
        if (yxmlLengthBefore > 0) {
          console.error(`🚨 [UNEXPECTED-STATE] ${docId.slice(-8)} - yxml has ${yxmlLengthBefore} elements BEFORE backend apply!`);
          console.error(`🚨 [UNEXPECTED-STATE] This should not happen - provider is disconnected during init`);
          console.error(`🚨 [UNEXPECTED-STATE] Clearing and applying backend (source of truth)...`);
          
          // Clear unexpected content before applying backend
          doc.transact(() => {
            yxml.delete(0, yxmlLengthBefore);
          }, 'backend-override');
        }
        
        log.log('[initializeDocumentContent] Applying backend Y snapshot (base64) - SOURCE OF TRUTH', {
          base64Length: initialBase64.length,
          base64Preview: initialBase64.substring(0, 50) + '...',
          yxmlLengthBefore,
          unexpectedContent: yxmlLengthBefore > 0
        });
        
        try {
          const bin = atob(initialBase64);
          const update = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) {
            update[i] = bin.charCodeAt(i);
          }
          
          log.log(formatJSON({
            docId: docId.slice(-8),
            base64Length: initialBase64.length,
            binaryLength: bin.length,
            updateArrayLength: update.length,
            yxmlLengthBefore: yxmlLengthBefore,
            base64Preview: initialBase64
          }, `🔍 [BASE64_DECODE:${docId.slice(-8)}] About to apply Y.js update`, { maxStringLength: 200 }));
          
          Y.applyUpdateV2(doc, update);
          
          // Inspect Y.XmlFragment structure in detail
          const yxmlChildren = Array.from({ length: Math.min(yxml.length, 10) }, (_, i) => {
            const el = yxml.get(i);
            if (el instanceof Y.XmlElement) {
              const firstChild = el.length > 0 ? el.get(0) : null;
              return {
                index: i,
                type: el.nodeName,
                attributes: el.getAttributes(),
                childCount: el.length,
                textContent: el.toString(),
                firstChild: firstChild ? {
                  type: firstChild instanceof Y.XmlElement ? (firstChild as Y.XmlElement).nodeName : 'YXmlText',
                  content: firstChild.toString()
                } : null
              };
            } else {
              return {
                index: i,
                type: 'YXmlText',
                textContent: el.toString()
              };
            }
          });
          
          log.log(formatJSON({
            docId: docId.slice(-8),
            yxmlLengthAfter: yxml.length,
            yxmlChildren,
            docClientId: doc.clientID,
            docGuid: doc.guid
          }, `🔍 [BASE64_DECODE:${docId.slice(-8)}] Y.js update applied - FULL STRUCTURE`, { maxObjectDepth: 5 }));
          
          log.log('[initializeDocumentContent] ✅ Backend Y.js base64 applied successfully (SOURCE OF TRUTH)', {
            yxmlLengthAfter: yxml.length,
            elementsLoaded: yxml.length,
            hadUnexpectedContent: yxmlLengthBefore > 0
          });
          
          // Success - document loaded from backend (source of truth)
          if (yxml.length > 0) {
            this.expectedDocumentSizes.set(docId, yxml.length);
            return;
          }
        } catch (base64Error) {
          // Backend base64 is corrupted - mark document and let user know
          console.error(`🚨 [CORRUPTION] ${docId.slice(-8)} - Backend base64 corrupted:`, base64Error);
          this.corruptedDocuments.add(docId);
          log.warn('[initializeDocumentContent] Document marked as corrupted', { docId });
        }
      } else {
        // No base64 provided - this is a new document
        log.log('[initializeDocumentContent] No backend base64 (new document)', {
          hasBase64: !!initialBase64,
          yxmlLength: yxml.length
        });
      }
      
      // ✅ FIX: If we reach here without base64, this is a new/empty document
      if (!initialBase64 && initialContent) {
        // 🛡️ GUARD: Check if document already has content to prevent double insertion
        if (yxml.length > 0) {
          log.log('[initializeDocumentContent] ⚠️ Document already has content, skipping re-initialization', {
            docId: docId.slice(-8),
            existingNodes: yxml.length,
            reason: 'duplicate_initialization_prevented'
          });
          return;
        }
        
        log.log('[initializeDocumentContent] No Y.js binary available, treating as new document with initial content');
        
        try {
          // 🆕 NEW: Check if content is ProseMirror JSON (from Google Docs import or other sources)
          const isProseMirrorJson = typeof initialContent === 'object' && 
                                   initialContent !== null && 
                                   'type' in initialContent && 
                                   initialContent.type === 'doc';
          
          if (isProseMirrorJson) {
            log.log('[initializeDocumentContent] Detected ProseMirror JSON format', {
              hasContent: !!(initialContent as any).content,
              nodeCount: (initialContent as any).content?.length || 0
            });
            
            // Import ProseMirror utilities
            const { schema } = await import('../../editor/schema');
            const { Node } = await import('prosemirror-model');
            
            // Convert JSON to ProseMirror node
            const pmDoc = Node.fromJSON(schema, initialContent);
            
            log.log('[initializeDocumentContent] Converted ProseMirror JSON to node:', {
              nodeCount: pmDoc.childCount,
              docSize: pmDoc.nodeSize
            });
            
            // Convert ProseMirror doc to Y.js manually by creating Y.XmlElements
            // This is more reliable than using internal y-prosemirror functions
            doc.transact(() => {
              // Helper function to recursively convert ProseMirror nodes to Y.js
              const convertNodeToYXml = (pmNode: any): any => {
                if (pmNode.isText) {
                  return new Y.XmlText(pmNode.text);
                }
                
                const yElement = new Y.XmlElement(pmNode.type.name);
                
                // Set attributes
                if (pmNode.attrs) {
                  for (const [key, value] of Object.entries(pmNode.attrs)) {
                    if (value !== null && value !== undefined) {
                      yElement.setAttribute(key, String(value));
                    }
                  }
                }
                
                // Set marks as attributes
                if (pmNode.marks && pmNode.marks.length > 0) {
                  for (const mark of pmNode.marks) {
                    yElement.setAttribute(`mark_${mark.type.name}`, JSON.stringify(mark.attrs || {}));
                  }
                }
                
                // Recursively add children
                if (pmNode.content) {
                  pmNode.content.forEach((child: any) => {
                    const yChild = convertNodeToYXml(child);
                    yElement.insert(yElement.length, [yChild]);
                  });
                }
                
                return yElement;
              };
              
              // Convert all top-level nodes and insert into yxml
              pmDoc.content.forEach((childNode: any) => {
                const yChild = convertNodeToYXml(childNode);
                yxml.insert(yxml.length, [yChild]);
              });
            }, 'google-docs-import');
            
            log.log('[initializeDocumentContent] ✅ Initialized document from ProseMirror JSON', {
              yxmlLength: yxml.length,
              pmDocSize: pmDoc.nodeSize,
              pmContentChildCount: pmDoc.content.childCount
            });
            
            if (includeWelcomeImage) {
              this.addImageToDocument(yxml, WELCOME_IMAGE_URL, docId);
            }
            
            return;
          }
          
          // Handle string content (HTML or Markdown)
          if (typeof initialContent !== 'string') {
            log.error('[initializeDocumentContent] Invalid content type:', typeof initialContent);
            throw new Error(`Expected string or ProseMirror JSON, got ${typeof initialContent}`);
          }
          
          const looksLikeHtml = /<\w+[^>]*>/.test(initialContent) || initialContent.trim().startsWith('<');
          if (looksLikeHtml) {
            // Use same path as paste: HTML → PM → y-prosemirror → Y.js
            // This ensures colors, alignment, and all formatting preserved correctly
            const htmlConvertStart = Date.now();
            log.log('[initializeDocumentContent] ⏱️ Starting HTML→PM→Y.js conversion (same as paste):', {
              htmlLength: initialContent.length,
              htmlPreview: initialContent.substring(0, 100)
            });
            
            // Parse HTML to ProseMirror (already normalized, no double-normalization)
            const container = document.createElement('div');
            container.innerHTML = initialContent;
            const pmDoc = PMDOMParser.fromSchema(schema).parse(container);
            
            log.log('[initializeDocumentContent] ⏱️ ProseMirror doc created:', {
              nodeCount: pmDoc.childCount,
              docSize: pmDoc.nodeSize
            });
            
            // Use y-prosemirror's conversion (same as paste/editor insert)
            const { prosemirrorToYXmlFragment } = await import('y-prosemirror');
            
            const transactStart = Date.now();
            doc.transact(() => {
              // Clear yxml if it has content (shouldn't, but defensive)
              if (yxml.length > 0) {
                yxml.delete(0, yxml.length);
              }
              
              // Use y-prosemirror's converter (battle-tested, handles all marks/attrs)
              prosemirrorToYXmlFragment(pmDoc, yxml);
            }, 'html-initialization');
            
            const totalDuration = Date.now() - htmlConvertStart;
            log.log('[initializeDocumentContent] ✅ Initialized document via y-prosemirror (same as paste):', {
              duration: totalDuration,
              yxmlLength: yxml.length,
              pmNodeCount: pmDoc.childCount
            });
          } else {
            // Handle markdown content for new documents
            const seed = createYElement('paragraph', { logPrefix: '[docsCRDT-NewDocMD]', docId });
            yxml.insert(0, [seed]);
            
            const ctx: any = { doc, yxml, docId };
            docReplaceWithMarkdown(ctx, { all: true }, initialContent, {
              suggestionMode: false,
              batchDescription: 'new document markdown initialization'
            } as any);
            log.log('[initializeDocumentContent] ✅ Initialized new document with markdown');
          }
          
          if (includeWelcomeImage) {
            this.addImageToDocument(yxml, WELCOME_IMAGE_URL, docId);
          }
        } catch (error) {
          log.error('[initializeDocumentContent] Error initializing new document:', error);
          throw error;
        }
      } else if (!initialBase64 && !initialContent) {
        log.log('[initializeDocumentContent] Creating empty document with default styling');
        // Create a minimal empty document - rely on doc_style defaults for font
        // Don't add font marks here, let the document default font apply
        const emptyParagraph = createYElement('paragraph', { 
          logPrefix: '[docsCRDT-Empty]', 
          docId
        });
        yxml.insert(0, [emptyParagraph]);
      }
      
      // Apply page margins if provided (from Word import)
      if (pageMargins) {
        const styleMap = doc.getMap('doc_style');
        doc.transact(() => {
          styleMap.set('pageMarginTop', pageMargins.top);
          styleMap.set('pageMarginRight', pageMargins.right);
          styleMap.set('pageMarginBottom', pageMargins.bottom);
          styleMap.set('pageMarginLeft', pageMargins.left);
        }, 'page-margins-init');
        
        log.log('✅ [PAGE_MARGINS] Applied from Word import:', pageMargins);
      } else {
        log.log('⚠️ [PAGE_MARGINS] No page margins provided (using defaults)');
      }
      
      log.log('[initializeDocumentContent] ✅ Document initialization completed for:', docId);
    } catch (error) {
      log.error('[initializeDocumentContent] ❌ CRITICAL: Document initialization failed:', error);
      throw error; // Re-throw to fail loudly instead of silently creating empty docs
    }
  }

  // Debug utility to test multiplayer functionality
  public debugMultiplayer(docId: string) {
    const entityType = this.entityTypes.get(docId);
    const docKey = this.getDocKey(docId, entityType);
    const provider = this.crdt.getProvider(docKey);
    
    if (!provider) {
      log.error('[DEBUG] No provider found for doc:', docId);
      return null;
    }
    
    const awarenessStates = provider.awareness.getStates();
    const debug = {
      docId: docId.slice(-8),
      roomName: provider.roomname,
      websocketUrl: provider.url,
      connected: provider.wsconnected,
      synced: provider.synced,
      awarenessStatesCount: awarenessStates.size,
      localState: provider.awareness.getLocalState(),
      allStates: Array.from(awarenessStates.entries()).map(([clientId, state]) => ({
        clientId,
        user: state.user,
        cursor: state.cursor
      }))
    };
    
    log.log('[DEBUG] Multiplayer state for doc:', debug);
    log.log('🔍 Multiplayer Debug:', debug);
    
    return debug;
  }

  /**
   * Helper method to add an image to a Y.js document
   */
  private addImageToDocument(yxml: Y.XmlFragment, imageUrl: string, docId?: string): void {
    try {
      log.log('[docsCRDT] Adding welcome image to document');
      
      // Create the image element as a block node (not inside a paragraph)
      const imageElement = new Y.XmlElement('image');
      imageElement.setAttribute('src', imageUrl);
      imageElement.setAttribute('alt', 'Beautiful river landscape - Welcome to River');
      imageElement.setAttribute('title', 'River - Your AI-powered document editor');
      imageElement.setAttribute('width', '700'); // Set width, let height scale naturally to maintain aspect ratio
      imageElement.setAttribute('alignment', 'center'); // Center the welcome image
      imageElement.setAttribute('borderRadius', '0'); // No border radius by default
      imageElement.setAttribute('shadow', 'none'); // No shadow by default
      
      // Add the image directly as a block element
      yxml.insert(yxml.length, [imageElement]);
      
      log.log('[docsCRDT] Successfully added welcome image to document');
    } catch (error) {
      log.error('[docsCRDT] Failed to add welcome image:', error);
      // Don't throw - we don't want image insertion failure to break document creation
    }
  }

  /**
   * 🛡️ CRITICAL SAVE: Validates and saves document with data loss prevention
   * Returns the base64 string if successful, null if validation fails
   */
  private async performValidatedSave(docId: string, yxml: Y.XmlFragment, options?: { 
    isRetry?: boolean;  // 🛡️ Track if this is a retry attempt
    isVersionRetry?: boolean;  // 🔥 Track if this is specifically a version conflict retry
  }): Promise<string | null> {
    const startTime = Date.now();
    
    // ✅ Declare variables outside try block so they're accessible in catch
    const elementCount = yxml.length;
    let base64 = '';
    
    try {
      // 🛡️ CRITICAL: Block empty document saves to prevent data loss
      
      if (elementCount === 0) {
        console.error(`🚨 [SAVE-BLOCKED] ${docId.slice(-8)} - REFUSING to save empty document`);
        
        // 🛡️ ADDITIONAL CHECK: Verify backend doesn't have content we're about to overwrite
        try {
          const backendEntity = await entityService.get(docId);
          if (backendEntity?.skeleton?.base64 && backendEntity.skeleton.base64.length > 100) {
            console.error(`🚨🚨🚨 [DATA-LOSS-PREVENTION] ${docId.slice(-8)} - Backend has ${backendEntity.skeleton.base64.length} bytes but trying to save empty!`);
            console.error(`🚨🚨🚨 [DATA-LOSS-PREVENTION] This would cause PERMANENT DATA LOSS - save absolutely blocked!`);
            // Absolutely block this save
          }
        } catch (e) {
          // If backend check fails, still block empty save
          console.warn(`⚠️ [SAVE-BLOCKED] Backend verification failed, but still blocking empty save`, e);
        }
        
        return null; // BLOCK the save
      }
      
      // 🛡️ CORRUPTION CHECK: Block saves for documents with corrupted base64
      if (this.corruptedDocuments.has(docId)) {
        console.error(`🚨🚨🚨 [SAVE-BLOCKED-CORRUPTION] ${docId.slice(-8)} - Document has corrupted base64 in backend`);
        console.error(`🚨🚨🚨 [SAVE-BLOCKED-CORRUPTION] Blocking ALL saves to prevent overwriting potentially recoverable data`);
        console.error(`🚨🚨🚨 [MANUAL-RECOVERY-REQUIRED] This document needs manual recovery by administrator`);
        
        // TODO: Show user-facing error toast or modal
        // For now, just block silently and log loudly
        return null;
      }

      log.log('[performValidatedSave] Saving', { docId: docId.slice(-8), elements: elementCount });

      // Skip all HTML conversion - only save Y.js binary with V2 consistency
      if (yxml.doc) {
        const update = Y.encodeStateAsUpdateV2(yxml.doc);
        
        // ✅ OPTIMIZED: Use array.join() instead of string concatenation for better performance
        const uint8Array = new Uint8Array(update);
        const binaryChunks: string[] = [];
        const chunkSize = 8192; // Process in 8KB chunks to avoid stack overflow
        
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
          const chunk = uint8Array.slice(i, i + chunkSize);
          binaryChunks.push(String.fromCharCode(...chunk));
        }
        const binaryString = binaryChunks.join('');
        
        base64 = btoa(binaryString);
      }
      
      // ✅ NEW: Compute metadata (nodeCount, wordCount) from Y.js document
      const nodeCount = yxml.length;
      // ✅ CRITICAL FIX: Skip word count to avoid infinite loop
      // getTextContent() calls initProseMirrorDoc() which triggers Y.js transactions
      // This causes observer → save → getTextContent() → infinite loop!
      // Word count is nice-to-have, not critical for saves
      let wordCount = 0;  // Skip word count computation to prevent infinite loops
      
      // ✅ ARCHITECTURE: Direct backend save for document content (not entity metadata)
      // Document content (base64) is stripped from entityStore cache, so it should NOT
      // flow through the entity mutation funnel. This is a CRDT layer saving to backend.
      
      // ✅ FIX #2: Save to backend FIRST, update cache ONLY after success
      // This prevents phantom "saved" state when backend save fails
      
      log.log(`📝 [SAVE-DEBUG] ${docId.slice(-8)} - Attempting save:`, {
        elementCount,
        base64Length: base64.length,
        isRetry: !!options?.isRetry
      });
      
      const skeletonPayload: Record<string, unknown> = {
        '@type': 'Doc',
        metadata: {
          nodeCount,
          wordCount,
          lastSaved: new Date().toISOString()
        }
      };
      if (!SERVER_IS_DURABILITY_BOUNDARY) {
        skeletonPayload.base64 = base64;
      }
      await entityService.mergeUpdate(docId, {
        skeleton: skeletonPayload
      });
      
      log.log(`✅ [SAVE-DEBUG] ${docId.slice(-8)} - Save succeeded!`);
      
      // ✅ Backend save succeeded! Now safe to update local state
      
      // Note: Version tracking temporarily disabled due to corrupted versions
      // Will re-enable after version reset
      
      const duration = Date.now() - startTime;
      
      log.log('[performValidatedSave] ✅ Backend save successful', {
        docId: docId.slice(-8),
        base64Length: base64.length,
        elements: elementCount,
        duration
      });
      
      this.expectedDocumentSizes.set(docId, elementCount);
      
      // 🔥 CRITICAL FIX: Invalidate request cache after save to ensure next fetch gets fresh data
      // Without this, page refresh might load stale cached response from before the save
      const { requestCacheService } = await import('../../../../services/requestCacheService');
      const { resolveToActualId } = await import('../../../../utils/llmIdMapper');
      const actualId = resolveToActualId(docId);
      requestCacheService.invalidate(`entity-${actualId}`);
      log.log('[performValidatedSave] Invalidated request cache', { docId: docId.slice(-8), cacheKey: `entity-${actualId}` });
      
      // 🔥 CRITICAL FIX: Update EntityStore cache with fresh metadata
      // ⚠️ IMPORTANT: This block only runs if backend save succeeded (no error thrown above)
      // Without this, AI context builder reads stale metadata from cache
      try {
        log.log('[METADATA_CACHE_UPDATE] ========== Starting cache update ==========');
        log.log('[METADATA_CACHE_UPDATE] Document info:', {
          docId: docId.slice(-8),
          actualId: actualId.slice(-8),
          nodeCount,
          wordCount
        });
        
        const { default: useEntityStore } = await import('../../../../stores/entityStore');
        const entityStore = useEntityStore.getState();
        
        log.log('[METADATA_CACHE_UPDATE] EntityStore loaded:', {
          totalRootEntities: entityStore.entities.length
        });
        
        // Flatten hierarchy to search all entities (not just root level)
        const flattenEntities = (entities: any[]): any[] => {
          const result: any[] = [];
          const traverse = (entity: any) => {
            const { children, ...entityWithoutChildren } = entity;
            result.push(entityWithoutChildren);
            if (children && Array.isArray(children)) {
              children.forEach(traverse);
            }
          };
          entities.forEach(traverse);
          return result;
        };
        
        const flatEntities = flattenEntities(entityStore.entities);
        log.log('[METADATA_CACHE_UPDATE] Flattened hierarchy:', {
          hierarchicalCount: entityStore.entities.length,
          flatCount: flatEntities.length
        });
        
        const cachedEntity = flatEntities.find(e => 
          e._id === actualId || e._id === docId
        );
        
        if (cachedEntity) {
          log.log('[METADATA_CACHE_UPDATE] ✅ Entity found in cache:', {
            entityId: cachedEntity._id.slice(-8),
            entityName: cachedEntity.name,
            entityType: cachedEntity.entityType,
            hasSkeleton: !!cachedEntity.skeleton,
            oldMetadata: cachedEntity.skeleton?.metadata
          });
          
          // 🔍 Create timestamp ONCE to ensure consistency across update and logs
          const updateTimestamp = new Date().toISOString();
          const newMetadata = {
            nodeCount,
            wordCount,
            lastSaved: updateTimestamp
          };
          
          const updatedEntity = {
            ...cachedEntity,
            skeleton: {
              ...(cachedEntity.skeleton || {}),  // ✅ Safe spread - handles undefined skeleton
              metadata: newMetadata
            },
            updated_at: updateTimestamp
          };
          
          // 🔍 DEBUG: Log what we're about to send to _applyLocalUpsert
          log.log('[METADATA_CACHE_UPDATE] 🔧 Calling _applyLocalUpsert with:', {
            entityId: updatedEntity._id.slice(-8),
            hasMetadata: !!updatedEntity.skeleton?.metadata,
            metadata: updatedEntity.skeleton?.metadata
          });
          
          entityStore._applyLocalUpsert(updatedEntity, {
            log: false,  // Silent update (performance save)
            source: 'local',
            syncCRDT: false  // Don't sync back (already saved to backend)
          });
          
          log.log('[METADATA_CACHE_UPDATE] ✅ Cache update attempted:', {
            docId: docId.slice(-8),
            newMetadata
          });
          
          // 🔍 CRITICAL: Get FRESH EntityStore state after _applyLocalUpsert
          // This ensures we're reading the CURRENT state, not a stale reference
          const freshEntityStore = useEntityStore.getState();
          const updatedFlatEntities = flattenEntities(freshEntityStore.entities);
          const verifyEntity = updatedFlatEntities.find(e => 
            e._id === actualId || e._id === docId
          );
          
          const verifiedMetadata = verifyEntity?.skeleton?.metadata;
          const matches = verifiedMetadata?.nodeCount === nodeCount && 
                         verifiedMetadata?.wordCount === wordCount;
          
          log.log('[METADATA_CACHE_UPDATE] ✅ Verification check:', {
            expected: newMetadata,  // ✅ Show full expected metadata including timestamp
            actual: verifiedMetadata,
            matches,
            matchDetails: {
              nodeCountMatch: verifiedMetadata?.nodeCount === nodeCount,
              wordCountMatch: verifiedMetadata?.wordCount === wordCount,
              hasLastSaved: !!verifiedMetadata?.lastSaved
            }
          });
          
          // 🚨 If verification failed, log detailed debugging
          if (!matches) {
            console.error('[METADATA_CACHE_UPDATE] ❌ VERIFICATION FAILED - Update did NOT persist!', {
              expected: newMetadata,
              actual: verifiedMetadata,
              oldMetadata: cachedEntity.skeleton?.metadata,
              entityId: verifyEntity?._id?.slice(-8),
              hasSkeleton: !!verifyEntity?.skeleton,
              hasMetadata: !!verifiedMetadata,
              // 🔍 Deep dive into what changed
              comparison: {
                nodeCount: {
                  old: cachedEntity.skeleton?.metadata?.nodeCount,
                  expected: nodeCount,
                  actual: verifiedMetadata?.nodeCount
                },
                wordCount: {
                  old: cachedEntity.skeleton?.metadata?.wordCount,
                  expected: wordCount,
                  actual: verifiedMetadata?.wordCount
                }
              },
              possibleCauses: [
                'Race condition: HierarchyStore overwrote our changes during rebuild',
                'Zustand state not fully committed',
                'normalizeEntityForCache stripped metadata',
                'Entity reference changed during _applyLocalUpsert'
              ]
            });
          }
          
          log.log('[performValidatedSave] ✅ Updated EntityStore cache with fresh metadata', {
            docId: docId.slice(-8),
            nodeCount,
            wordCount
          });
        } else {
          console.warn('[METADATA_CACHE_UPDATE] ⚠️ Entity not found in cache:', {
            docId: docId.slice(-8),
            actualId: actualId.slice(-8),
            searchedInTotal: flatEntities.length,
            availableEntityIds: flatEntities.slice(0, 10).map(e => ({
              id: e._id?.slice(-8),
              name: e.name,
              type: e.entityType
            }))
          });
          
          log.warn('[performValidatedSave] Entity not found in cache:', {
            docId: docId.slice(-8)
          });
        }
        
        log.log('[METADATA_CACHE_UPDATE] ========== Cache update complete ==========');
      } catch (error) {
        console.error('[METADATA_CACHE_UPDATE] ❌ Failed to update EntityStore cache:', {
          error: error instanceof Error ? error.message : error,
          stack: error instanceof Error ? error.stack : undefined,
          docId: docId.slice(-8)
        });
        
        log.warn('[performValidatedSave] Failed to update EntityStore cache:', error);
        // Non-critical: EntityStore will eventually refresh from backend
      }
      
      // 🔥 CRITICAL FIX: Small delay to ensure backend write is fully committed before next read
      // MongoDB writes are eventually consistent - this ensures read-after-write consistency
      // Only wait if this is a content save (base64), not metadata-only updates
      if (base64.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 100)); // 100ms delay for write propagation
        log.log('[performValidatedSave] Write propagation delay completed', { docId: docId.slice(-8) });
      }
      
      // 🛡️ LAYER 3: Clear any previous unsaved warning on successful save
      if (document.title.startsWith('⚠️ UNSAVED - ')) {
        document.title = document.title.replace('⚠️ UNSAVED - ', '');
        console.warn(`✅ [SAVE-SUCCESS] Cleared unsaved warning from tab title`);
      }
      
      // 🔄 SYNC CHECK: Verify WebSocket is connected for real-time sync
      // The save to EntityService is complete, but WebSocket sync happens separately
      const entityType = this.entityTypes.get(docId);
      const docKey = this.getDocKey(docId, entityType);
      const provider = this.crdt.getProvider(docKey);
      if (provider && !provider.wsconnected) {
        console.warn(`⚠️ [SYNC-WARNING] ${docId.slice(-8)} - EntityService save complete but WebSocket disconnected`);
        console.warn(`⚠️ [SYNC-WARNING] Content saved to backend but may not sync to other clients until reconnect`);
      } else if (!provider) {
        console.warn(`⚠️ [SYNC-WARNING] ${docId.slice(-8)} - No WebSocket provider found`);
      }

      // ✅ CRITICAL FIX: Skip summary update to avoid infinite loop
      // getTextContent() calls initProseMirrorDoc() which triggers Y.js transactions
      // This causes observer → save → getTextContent() → infinite loop!
      // Summary updates are nice-to-have, not critical for saves
      // After successful save, schedule summary update
      // try {
      //   const textContent = this.getTextContent(yxml);
      //   summaryService.scheduleSummaryUpdate(docId, textContent);
      // } catch (summaryError) {
      //   log.warn(`[docsCRDT] Failed to schedule summary update for doc ${docId}:`, summaryError);
      // }
      
      return base64;
      
    } catch (error) {
      const duration = Date.now() - startTime;
      
      // ✅ FIX #7: Enhanced error logging with detailed context
      const errorContext = {
        docId: docId.slice(-8),
        duration,
        elementCount,
        base64Length: base64?.length || 0,
        errorType: error?.constructor?.name || 'Unknown',
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStatus: (error as any)?.status,
        // Network diagnostics
        isOnline: navigator.onLine,
        connectionType: (navigator as any)?.connection?.effectiveType,
        // Error categorization
        isNetworkError: !navigator.onLine || 
                       (error instanceof Error && (
                         error.message.includes('network') || 
                         error.message.includes('fetch') ||
                         error.message.includes('Failed to fetch')
                       )),
        isAuthError: (error as any)?.status === 401 || (error as any)?.status === 403,
        isServerError: (error as any)?.status >= 500,
        isClientError: (error as any)?.status >= 400 && (error as any)?.status < 500
      };
      
      console.error(`❌ [ENTITY-SAVE-FAIL] ${docId.slice(-8)} - EntityService save FAILED`, errorContext);
      console.error(`⚠️⚠️⚠️ [SAVE-FAILED] DO NOT CLOSE THIS TAB - YOUR CHANGES ARE NOT SAVED! ⚠️⚠️⚠️`);
      console.error(`⚠️⚠️⚠️ [SAVE-FAILED] Keep this tab open until you see save confirmation`);
      
      // Note: Version conflict handling removed (version checking disabled)
      // Will re-enable after version corruption is fixed
      
      // ✅ User-friendly error messages with specific guidance
      let userMessage = 'Failed to save document';
      let userDetails = '';
      let canRetry = true;
      
      if (!navigator.onLine) {
        userMessage = 'No internet connection';
        userDetails = 'Your changes will be saved when you reconnect.';
        canRetry = true;
      } else if (errorContext.isAuthError) {
        if ((error as any)?.status === 401) {
          userMessage = 'Session expired';
          userDetails = 'Please refresh the page and log in again.';
          canRetry = false;
        } else if ((error as any)?.status === 403) {
          userMessage = 'Permission denied';
          userDetails = 'You no longer have permission to edit this document.';
          canRetry = false;
        }
      } else if (errorContext.isServerError) {
        userMessage = 'Server error';
        userDetails = 'The server encountered an error. Please try again.';
        canRetry = true;
      } else if (errorContext.isNetworkError) {
        userMessage = 'Network error';
        userDetails = 'Please check your internet connection and try again.';
        canRetry = true;
      }
      
      // Show toast notification (will be implemented when toast system is available)
      // For now, log what the toast would say
      console.error(`[USER-NOTIFICATION] ${userMessage}${userDetails ? ': ' + userDetails : ''}`, {
        canRetry,
        retryAvailable: canRetry && !options?.isRetry
      });
      
      // 🛡️ LAYER 3: Change browser tab title to warn user
      if (!document.title.startsWith('⚠️ UNSAVED')) {
        document.title = '⚠️ UNSAVED - ' + document.title;
        console.error(`⚠️ [SAVE-FAILED] Tab title updated to warn user - visible in browser tab`);
      }
      
      // 🛡️ LAYER 3: Retry once after 5 seconds (catches transient network errors)
      if (!options?.isRetry) {
        console.warn(`🔄 [SAVE-RETRY] Will retry save in 5 seconds (general retry, not version conflict)...`);
        setTimeout(() => {
          console.warn(`🔄 [SAVE-RETRY] Executing delayed retry for ${docId.slice(-8)}...`);
          this.performValidatedSave(docId, yxml, { ...options, isRetry: true })
            .then((result) => {
              if (result) {
                console.warn(`✅ [SAVE-RETRY] Retry succeeded! Document saved.`);
              } else {
                console.error(`🚨 [SAVE-RETRY] Retry also failed - user intervention needed`);
              }
            })
            .catch((retryError) => {
              console.error(`🚨 [SAVE-RETRY] Retry threw error:`, retryError);
            });
        }, 5000);
      } else if (options?.isRetry && !options?.isVersionRetry) {
        console.error(`🚨 [SAVE-FAILED] General retry failed for ${docId.slice(-8)} - both attempts failed`);
        console.error(`🚨 [SAVE-FAILED] User must keep tab open. Y.js will sync via WebSocket.`);
      }
      
      log.error(`[docsCRDT] Failed to save content to DB for doc ${docId}:`, error);
      return null;
    }
  }

  // Get or create a per-document debounced save function
  private getDocumentDebouncedSave(docId: string): ReturnType<typeof debounce> {
    let debouncedFn = this.perDocumentDebouncedSaves.get(docId);
    
    if (!debouncedFn) {
      debouncedFn = debounce(async (yxml: Y.XmlFragment) => {
        try {
          await this.performValidatedSave(docId, yxml);
        } catch (error) {
          console.error(`[docsCRDT] Save failed for ${docId.slice(-8)}:`, error);
        }
      }, 500, {
        maxWait: 5000
      });
      
      this.perDocumentDebouncedSaves.set(docId, debouncedFn);
    }
    
    return debouncedFn;
  }
  
  private triggerDebouncedSave(docId: string, yxml: Y.XmlFragment): void {
    const debouncedFn = this.getDocumentDebouncedSave(docId);
    debouncedFn(yxml);
  }
  
  public flushDocumentSave(docId: string): void {
    const debouncedFn = this.perDocumentDebouncedSaves.get(docId);
    if (debouncedFn) {
      debouncedFn.flush();
    }
  }
  
  /**
   * ✅ FIX #6: Flush all pending saves for all documents
   * Used before critical state transitions (dataspace switch, app close)
   * Returns promise that resolves when all flushes complete
   */
  public async flushAllPendingSaves(): Promise<void> {
    log.log('[flushAllPendingSaves] Flushing all pending document saves', {
      documentCount: this.perDocumentDebouncedSaves.size
    });
    
    if (this.perDocumentDebouncedSaves.size === 0) {
      log.log('[flushAllPendingSaves] No pending saves to flush');
      return;
    }
    
    // Flush all debounced saves
    const flushPromises: Promise<void>[] = [];
    
    this.perDocumentDebouncedSaves.forEach((debouncedFn, docId) => {
      log.log('[flushAllPendingSaves] Flushing save for:', docId.slice(-8));
      
      // Flush returns the debounced function's return value (Promise<void>)
      const flushPromise = debouncedFn.flush();
      if (flushPromise && typeof flushPromise.then === 'function') {
        flushPromises.push(flushPromise);
      }
    });
    
    // Wait for all flushes to complete (with timeout)
    try {
      await Promise.race([
        Promise.all(flushPromises),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Flush timeout')), 5000)
        )
      ]);
      
      log.log('[flushAllPendingSaves] ✅ All saves flushed successfully');
    } catch (error) {
      if (error instanceof Error && error.message === 'Flush timeout') {
        log.error('[flushAllPendingSaves] ⚠️ Flush timeout after 5 seconds', {
          pendingCount: flushPromises.length
        });
      } else {
        log.error('[flushAllPendingSaves] ❌ Flush failed:', error);
      }
      throw error;
    }
  }
  
  // Force save document directly - bypasses debounce for guaranteed persistence
  public async forceSaveDocument(docId: string): Promise<boolean> {
    try {
      const entityType = this.entityTypes.get(docId);
      const docKey = this.getDocKey(docId, entityType);
      const doc = this.crdt.getYDoc(docKey);
      
      if (!doc) {
        console.error(`[docsCRDT] forceSaveDocument: No Y.Doc found for ${docId.slice(-8)}`);
        return false;
      }
      
      const yxml = doc.getXmlFragment('prosemirror');
      
      if (!yxml || yxml.length === 0) {
        return false;
      }
      
      const result = await this.performValidatedSave(docId, yxml);
      return !!result;
    } catch (error) {
      console.error(`[docsCRDT] forceSaveDocument failed for ${docId.slice(-8)}:`, error);
      return false;
    }
  }

  // Helper method to extract text content from Y.js
  private   getTextContent(yxml: Y.XmlFragment): string {
    // ✅ CRITICAL FIX: Use fragment object as guard key (works across all contexts)
    // initProseMirrorDoc() can trigger Y.js transactions, which fire observer,
    // which triggers save, which calls getTextContent() again = INFINITE LOOP!
    const fragmentKey = yxml as any;  // Use object reference as unique key
    
    if (this.isInTextExtraction.has(fragmentKey)) {
      console.warn(`⚠️ [REENTRY-GUARD] Skipping nested getTextContent() call (infinite loop prevention)`);
      return '';  // Return empty to break the loop
    }
    
    this.isInTextExtraction.add(fragmentKey);
    
    try {
      // Convert to ProseMirror and extract text with error handling
      try {
        const { doc } = initProseMirrorDoc(yxml, schema);
        return doc.textContent;
      } catch (syncError) {
        log.error('[docsCRDT] CRITICAL: Failed to extract text content via ProseMirror!', syncError);
        
        // 🚨 CRITICAL: DO NOT USE toString() FALLBACK - causes contamination!
        // The toString() method returns XML like <fontFamily>text</fontFamily>
        // This contaminates summaries, text extraction, and any other text-based operations
        
        throw new Error(`Critical failure extracting text content from YJS document: ${syncError instanceof Error ? syncError.message : 'Unknown error'}. ProseMirror conversion is required for safe text extraction.`);
        
        // ❌ REMOVED: Dangerous toString() fallback
        // This was a major source of persistent contamination in the system
      }
    } catch (error) {
      log.warn('[docsCRDT] Failed to extract text content, returning empty string:', error);
      // Fallback to empty string if all else fails
      return '';
    } finally {
      // ✅ Always remove reentry guard when done
      this.isInTextExtraction.delete(fragmentKey);
    }
  }

  getDocName(docId: string): string | undefined {
    const entityType = this.entityTypes.get(docId);
    const docKey = this.getDocKey(docId, entityType);
    const doc = this.crdt.getYDoc(docKey);
    if (doc) {
      // 🚨 CRITICAL FIX: DO NOT use doc.getText('name').toString() - Y.Text could contain formatting!
      try {
        const nameText = doc.getText('name');
        const anyNameText = nameText as any;
        if (anyNameText._content && typeof anyNameText._content === 'string') {
          return anyNameText._content;
        } else {
          console.warn('[docsCRDTService] 🚨 Using dangerous toString() for document name extraction!');
          const contaminated = nameText.toString();
          return contaminated
            .replace(/<fontFamily[^>]*>.*?<\/fontFamily>/g, '')
            .replace(/<[^>]*>/g, '')
            .replace(/fontFamily/g, '');
        }
      } catch (error) {
        console.error('[docsCRDTService] CRITICAL: Cannot extract document name safely:', error);
        return '';
      }
    }
    return undefined;
  }


  /**
   * Save comments to CRDT for a document
   */
  async saveComments(docId: string, commentsData: string): Promise<void> {
    try {
      const entityType = this.entityTypes.get(docId);
      const docKey = this.getDocKey(docId, entityType);
      const doc = this.crdt.ensureInitialized(docKey);
      
      if (doc) {
        const commentsText = doc.getText('comments');
        commentsText.delete(0, commentsText.length);
        commentsText.insert(0, commentsData);
        
        log.log('[saveComments] Saved comments to CRDT for doc:', docId);
      }
    } catch (error) {
      log.error('[saveComments] Failed to save comments:', error);
      throw error;
    }
  }

  /**
   * Load comments from CRDT for a document
   */
  async loadComments(docId: string): Promise<string | null> {
    try {
      const entityType = this.entityTypes.get(docId);
      const docKey = this.getDocKey(docId, entityType);
      const doc = this.crdt.ensureInitialized(docKey);
      
      if (doc) {
        const commentsText = doc.getText('comments');
        // 🚨 CRITICAL FIX: DO NOT use commentsText.toString() - Y.Text could contain formatting contamination!
        let commentsData = '';
        try {
          const anyCommentsText = commentsText as any;
          if (anyCommentsText._content && typeof anyCommentsText._content === 'string') {
            commentsData = anyCommentsText._content;
          } else {
            console.warn('[docsCRDTService] 🚨 Using dangerous toString() for comments extraction!');
            const contaminated = commentsText.toString();
            commentsData = contaminated
              .replace(/<fontFamily[^>]*>.*?<\/fontFamily>/g, '')
              .replace(/<[^>]*>/g, '')
              .replace(/fontFamily/g, '');
          }
        } catch (error) {
          console.error('[docsCRDTService] CRITICAL: Cannot extract comments safely:', error);
          commentsData = '';
        }
        
        if (commentsData) {
          log.log('[loadComments] Loaded comments from CRDT for doc:', docId);
          return commentsData;
        }
      }
      
      return null;
    } catch (error) {
      log.error('[loadComments] Failed to load comments:', error);
      return null;
    }
  }

  saveHistory(docId: string, undoManager: Y.UndoManager, ydoc: Y.Doc) {
    // Full implementation for saving the current state together with the
    // UndoManager history (undo / redo stacks) to the database.
    // Based on Y.js documentation: https://docs.yjs.dev/api/undo-manager

    try {
      // 1. Encode the full Y.Doc state so it can be restored later.
      const encodedState = Y.encodeStateAsUpdateV2(ydoc); // 🔧 V2 API consistency
      let encodedStateB64 = '';
      const _stateBytes = new Uint8Array(encodedState);
      for (let i = 0; i < _stateBytes.length; i += 8192) {
        encodedStateB64 += String.fromCharCode(..._stateBytes.subarray(i, i + 8192));
      }
      encodedStateB64 = btoa(encodedStateB64);

      // 2. Serialize undo & redo stacks using binary encoding instead of JSON
      // This preserves the complex Y.js internal structures properly
      const undoStackSerialized: string[] = undoManager.undoStack.map(stackItem => {
        try {
          // Convert DeleteSets to a serializable format
          const serializable = {
            deletions: this.encodeDeleteSet(stackItem.deletions),
            insertions: this.encodeDeleteSet(stackItem.insertions),
            meta: stackItem.meta ? Object.fromEntries(stackItem.meta) : {}
          };
          
          return JSON.stringify(serializable);
        } catch (error) {
          log.error('[docsCRDT] Failed to serialize undo stack item:', error);
          return '';
        }
      }).filter(item => item !== '');
      
      const redoStackSerialized: string[] = undoManager.redoStack.map(stackItem => {
        try {
          const serializable = {
            deletions: this.encodeDeleteSet(stackItem.deletions),
            insertions: this.encodeDeleteSet(stackItem.insertions),
            meta: stackItem.meta ? Object.fromEntries(stackItem.meta) : {}
          };
          
          return JSON.stringify(serializable);
        } catch (error) {
          log.error('[docsCRDT] Failed to serialize redo stack item:', error);
          return '';
        }
      }).filter(item => item !== '');

      const historyPayload = {
        ydoc: encodedStateB64,
        undo: undoStackSerialized,
        redo: redoStackSerialized,
      };

      // Persist the history under `skeleton.history` so that it lives next to the
      // regular document payload (HTML / base64) without breaking the current API.
      entityService.mergeUpdate(docId, {
        skeleton: {
          history: historyPayload,
        } as any, // `as any` keeps TypeScript happy until proper types are added.
      });

      log.log('[saveHistory] History saved successfully', {
        docId,
        undoItems: undoManager.undoStack.length,
        redoItems: undoManager.redoStack.length,
        payloadSize: JSON.stringify(historyPayload).length
      });
    } catch (error) {
      log.error('[saveHistory] Failed to save history', error);
    }
  }

  async loadHistory(docId: string, undoManager: Y.UndoManager, ydoc: Y.Doc) {
    // Full implementation that reads the previously persisted history from the DB 
    // and hydrates the provided UndoManager with it.
    // Based on Y.js documentation: https://docs.yjs.dev/api/undo-manager
    try {
      const entity: any = await entityService.get(docId);
      const history = entity?.skeleton?.history;

      if (!history) {
        log.warn('[docsCRDT] No history found in entity – skipping hydration', { docId });
        return;
      }

      // 1. Restore the main document state first so that we have a proper baseline.
      if (history.ydoc) {
        try {
        const bin = atob(history.ydoc);
        const update = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) {
          update[i] = bin.charCodeAt(i);
        }
        Y.applyUpdateV2(ydoc, update); // 🔧 V2 API consistency
        log.log('[loadHistory] Document state restored from history (V2)');
        } catch (decodingError) {
          log.error('[YJS-DECODE-ERROR] Failed to restore document state from history:', decodingError);
          log.error('[YJS-DECODE-ERROR] Skipping history restoration - document will load without undo/redo history');
          // Continue without history - better than crashing
        }
      }

      // 2. Clear existing stacks before restoring
      undoManager.undoStack.length = 0;
      undoManager.redoStack.length = 0;

      // 3. Re-populate undo stack using proper deserialization
      const undoUpdates: string[] = history.undo || [];
      undoUpdates.forEach((serializedStackItem) => {
        if (!serializedStackItem) return;
        
        try {
          const stackItemData = JSON.parse(serializedStackItem);
          
          // Reconstruct the StackItem with proper DeleteSets
          const stackItem = {
            deletions: this.decodeDeleteSet(stackItemData.deletions),
            insertions: this.decodeDeleteSet(stackItemData.insertions),
            meta: new Map(Object.entries(stackItemData.meta || {}))
          };
          
          undoManager.undoStack.push(stackItem as any);
        } catch (decodeError) {
          log.error('[docsCRDT] Failed to deserialize undo stack item', decodeError);
        }
      });

      // 4. Re-populate redo stack using proper deserialization
      const redoUpdates: string[] = history.redo || [];
      redoUpdates.forEach((serializedStackItem) => {
        if (!serializedStackItem) return;
        
        try {
          const stackItemData = JSON.parse(serializedStackItem);
          
          // Reconstruct the StackItem with proper DeleteSets
          const stackItem = {
            deletions: this.decodeDeleteSet(stackItemData.deletions),
            insertions: this.decodeDeleteSet(stackItemData.insertions),
            meta: new Map(Object.entries(stackItemData.meta || {}))
          };
          
          undoManager.redoStack.push(stackItem as any);
        } catch (decodeError) {
          log.error('[docsCRDT] Failed to deserialize redo stack item', decodeError);
        }
      });

      log.log('[loadHistory] History loaded successfully', {
        docId,
        undoItems: undoManager.undoStack.length,
        redoItems: undoManager.redoStack.length,
      });
    } catch (error) {
      log.error('[loadHistory] Failed to load history', error);
    }
  }

  private encodeDeleteSet(deleteSet: any): any {
    // Convert DeleteSet to a serializable format
    if (!deleteSet || !deleteSet.clients) {
      return { clients: {} };
    }
    
    const clientsObj: any = {};
    
    // Convert Map to object for JSON serialization
    if (deleteSet.clients instanceof Map) {
      deleteSet.clients.forEach((ranges: any[], client: number) => {
        clientsObj[client] = ranges;
      });
    } else if (typeof deleteSet.clients === 'object') {
      // Already an object
      Object.assign(clientsObj, deleteSet.clients);
    }
    
    return { clients: clientsObj };
  }

  private decodeDeleteSet(encodedDeleteSet: any): any {
    // Reconstruct DeleteSet from serialized format
    const deleteSet = {
      clients: new Map()
    };
    
    if (encodedDeleteSet && encodedDeleteSet.clients) {
      Object.entries(encodedDeleteSet.clients).forEach(([client, ranges]) => {
        deleteSet.clients.set(parseInt(client), ranges as any[]);
      });
    }
    
    return deleteSet;
  }

  /**
   * 🚀 Calculate document metadata without saving
   * Used to update EntityStore immediately after AI edits
   * 
   * @param docId The document ID to calculate metadata for
   * @returns Metadata object with nodeCount and wordCount, or null if document not found
   */
  public calculateDocumentMetadata(docId: string): { nodeCount: number; wordCount: number } | null {
    const yxml = this.yContentCache.get(docId);
    if (!yxml) {
      log.warn(`[calculateDocumentMetadata] No Y.js content found for ${docId}`);
      return null;
    }
    
    const nodeCount = yxml.length;
    let wordCount = 0;
    
    try {
      const textContent = this.getTextContent(yxml);
      wordCount = textContent.split(/\s+/).filter((w: string) => w.trim()).length;
    } catch (error) {
      log.warn('[calculateDocumentMetadata] Failed to compute word count:', error);
    }
    
    log.log('[calculateDocumentMetadata] Calculated metadata:', {
      docId: docId.slice(-8),
      nodeCount,
      wordCount
    });
    
    return { nodeCount, wordCount };
  }
}

export const docsCRDTService = new DocsCRDTService(crdtServiceWS);

  // Expose debug functions globally for testing
  if (typeof window !== 'undefined') {
    (window as any).debugDocMultiplayer = (docId: string) => docsCRDTService.debugMultiplayer(docId);
    
    // ✅ ROBUSTNESS: Health check function
    (window as any).checkMultiplayerHealth = () => {
      const docId = '68d4a05fa826cb5641d80113';
      const docKey = `doc:${docId}`;
      
      const provider = docsCRDTService.getProviderForTesting(docKey);
      const doc = docsCRDTService.getProviderForTesting(docKey)?.doc;
      
      const health = {
        docId: docId.slice(-8),
        hasProvider: !!provider,
        providerConnected: provider?.wsconnected || false,
        providerSynced: provider?.synced || false,
        wsReadyState: provider?.ws?.readyState,
        wsUrl: provider?.url,
        documentExists: !!doc,
        documentSize: doc?.getXmlFragment('prosemirror').length || 0,
        awarenessCount: provider?.awareness?.getStates?.()?.size || 0,
        globalLocks: Array.from(globalInitializationLock.keys()),
        timestamp: new Date().toISOString()
      };
      
      log.log('🏥 [MULTIPLAYER HEALTH CHECK]:', health);
      
      if (!provider || !provider.wsconnected) {
        console.warn('⚠️ [HEALTH] WebSocket connection issues detected');
      }
      
      if (health.awarenessCount < 2) {
        console.warn('⚠️ [HEALTH] Only', health.awarenessCount, 'users connected (expected 2+)');
      }
      
      return health;
    };
    (window as any).resetCorruptedDoc = () => {
      const docId = '68d4a05fa826cb5641d80113';
      
      if (docsCRDTService.resetDocument(docId)) {
        log.log('🔄 Reloading page to reinitialize document...');
        location.reload();
      }
    };
    
    (window as any).testDocSync = () => {
      const docId = '68d4a05fa826cb5641d80113';
      const docKey = `doc:${docId}`;
      
      log.log('🔍 Testing document sync for:', docId);
      
      // Get the provider from the underlying CRDT service
      const provider = docsCRDTService.getProviderForTesting(docKey);
      
      if (!provider) {
        log.log('❌ No provider found for document');
        return { error: 'No provider found' };
      }
      
      log.log('📡 Provider info:', {
        connected: provider.wsconnected,
        roomname: provider.roomname,
        url: provider.url,
        wsReadyState: provider.ws?.readyState,
        wsReadyStateText: provider.ws?.readyState === 0 ? 'CONNECTING' : 
                         provider.ws?.readyState === 1 ? 'OPEN' : 
                         provider.ws?.readyState === 2 ? 'CLOSING' : 
                         provider.ws?.readyState === 3 ? 'CLOSED' : 'UNKNOWN',
        wsUrl: provider.ws?.url
      });
      
      // Test by adding a simple text update to Y.js
      const doc = provider.doc;
      if (doc) {
        const testText = doc.getText('multiplayer-test');
        const timestamp = new Date().toISOString();
        testText.insert(0, `Test from client at ${timestamp}\n`);
        
        log.log('✅ Added test text to Y.js document. Check other client to see if it appears.');
        return {
          testAdded: true,
          timestamp,
          currentContent: testText.toString(),
          providerConnected: provider.wsconnected
        };
      }
      
      return { error: 'No Y.Doc found' };
    };
    
    // Quick test function
    (window as any).testMultiplayerQuick = () => {
      // Get the current document ID from URL (supports both /docs/ID and spaces with openEntity)
      let docId = null;
      
      // Try standalone docs URL pattern
      const standaloneMatch = window.location.pathname.match(/\/docs\/([a-f0-9]+)/);
      if (standaloneMatch) {
        docId = standaloneMatch[1];
      }
      
      // Try space URL with openEntity parameter
      if (!docId) {
        const openEntityMatch = window.location.search.match(/openEntity=([a-f0-9]+)/);
        if (openEntityMatch) {
          docId = openEntityMatch[1];
        }
      }
      
      // Try to get from active window state
      if (!docId) {
        try {
          // Access spaceStore from global window object
          const spaceStore = (window as any).__spaceStore || 
                           (window as any).useSpaceStore?.getState?.() ||
                           useSpaceStore.getState();
          
          if (spaceStore) {
            const state = typeof spaceStore === 'function' ? spaceStore() : spaceStore;
            const activeWindow = state.activeWindowId ? state.windows[state.activeWindowId] : null;
            
            if (activeWindow?.appType === 'docs') {
              docId = activeWindow.applicationState?.docs?.activeDocId || 
                      activeWindow.entityId ||
                      (activeWindow.entity?._id || activeWindow.entity?.id);
              
              log.log('🔍 Found docId from space store:', {
                docId,
                activeWindowId: state.activeWindowId,
                windowAppType: activeWindow.appType
              });
            }
          }
        } catch (error) {
          console.warn('Could not get docId from space store:', error);
        }
      }
      
      if (docId) {
        log.log('🔍 Found document ID:', docId);
        return docsCRDTService.debugMultiplayer(docId);
      } else {
        log.log('❌ No document ID found. Make sure you have a document open.');
        log.log('💡 URL patterns supported:');
        log.log('  - /docs/DOCUMENT_ID');
        log.log('  - /spaces/SPACE_ID?openEntity=DOCUMENT_ID');
        log.log('  - /spaces/SPACE_ID (with active document window)');
        return null;
      }
    };
    
    // Debug image persistence
    (window as any).debugImagePersistence = () => {
      const urlMatch = window.location.pathname.match(/\/docs\/([a-f0-9]+)/);
      if (!urlMatch) {
        log.log('No document ID found in URL. Open a document first.');
        return null;
      }
      
      const docId = urlMatch[1];
      const provider = docsCRDTService.getProvider(docId);
      
      if (!provider) {
        log.log('No provider found for document');
        return null;
      }
      
      const doc = provider.doc;
      const yxml = doc.getXmlFragment('prosemirror');
      
      log.log('Y.js document structure:');
      for (let i = 0; i < yxml.length; i++) {
        const item = yxml.get(i);
        if (item instanceof Y.XmlElement) {
          log.log(`Item ${i}:`, {
            nodeName: item.nodeName,
            attributes: item.getAttributes(),
            textContent: item.toString().substring(0, 100)
          });
        } else {
          log.log(`Item ${i}:`, {
            type: 'YXmlText',
            textContent: item.toString().substring(0, 100)
          });
        }
      }
      
      return { yxmlLength: yxml.length };
    };
  
  // Test Y.js document sync (not just awareness)
  (window as any).testDocSync = () => {
    // Use the same logic as testMultiplayerQuick to find docId
    let docId = null;
    
    // Try standalone docs URL pattern
    const standaloneMatch = window.location.pathname.match(/\/docs\/([a-f0-9]+)/);
    if (standaloneMatch) {
      docId = standaloneMatch[1];
    }
    
    // Try space URL with openEntity parameter
    if (!docId) {
      const openEntityMatch = window.location.search.match(/openEntity=([a-f0-9]+)/);
      if (openEntityMatch) {
        docId = openEntityMatch[1];
      }
    }
    
    // Try to get from active window state
    if (!docId) {
      try {
        // Access spaceStore from global window object
        const spaceStore = (window as any).__spaceStore || 
                         (window as any).useSpaceStore?.getState?.() ||
                         useSpaceStore.getState();
        
        if (spaceStore) {
          const state = typeof spaceStore === 'function' ? spaceStore() : spaceStore;
          const activeWindow = state.activeWindowId ? state.windows[state.activeWindowId] : null;
          
          if (activeWindow?.appType === 'docs') {
            docId = activeWindow.applicationState?.docs?.activeDocId || 
                    activeWindow.entityId ||
                    (activeWindow.entity?._id || activeWindow.entity?.id);
          }
        }
      } catch (error) {
        console.warn('Could not get docId from space store:', error);
      }
    }
    
    if (!docId) {
      log.log('❌ No document ID found. Make sure you have a document open.');
      return null;
    }
    
    log.log('🔍 Testing document sync for:', docId);
    const provider = docsCRDTService.getProvider(docId);
    
    if (!provider) {
      log.log('No provider found for document');
      return null;
    }
    
    // Test by adding a simple text update to Y.js
    const doc = provider.doc;
    if (doc) {
      const testText = doc.getText('multiplayer-test');
      const timestamp = new Date().toISOString();
      testText.insert(0, `Test from client at ${timestamp}\n`);
      
      log.log('Added test text to Y.js document. Check other client to see if it appears.');
      return {
        testAdded: true,
        timestamp,
        currentContent: testText.toString()
      };
    }
    
    return { error: 'No Y.Doc found' };
  };
} 